'use client';

// Раздел «Сообщения» — список переписок ботов клиента (docs/MESSAGES-PLAN.md, М5).
// Данные — /api/cabinet/messages: последние диалоги обоих каналов, счётчик непрочитанных
// и список аккаунтов для селекта-фильтра в шапке (его рисует shell.tsx — он же присылает
// сюда выбранный канал/аккаунт пропсами и строку поиска событием scr-msg-q, как у «Моделей»).
// Вёрстка — канон UI-TEMPLATE-CANON: пузырь .ai-vgroup, канонический ряд .scn-trow
// (кружок 40 · имя · подпись · правая колонка со временем и непрочитанными).
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { KActionBar, KButton, KCheckbox, KEmpty } from '@scriptera/ui/kit';
import { useMessagesLive } from '@/lib/messages-live';
// Журнал клиента (DIAGNOSTICS-CANON): по жалобе «открылось не на том сообщении» первый
// ход — журнал, а не догадка.
import { log as journalLog } from '@/lib/client-journal';
import { FoldersStrip, useDialogFolders, useFoldersSettings } from './messages-folders';
import { HeadFolders } from './list-head';
import { FolderPage, useFolderPager } from './folder-pager';
import {
  peekPersistent,
  refreshPersistent,
  usePersistentData,
  usePersistentPeek,
} from '@/lib/use-persistent-data';
import { nativeBridge, nativeMethod, type NativeMsgsRow } from '@/lib/native-app';
import { nativeMsgsSupported, useNativeMsgs } from '@/lib/msgs-native';
import {
  DIALOG_TAB_EVENT,
  OPEN_DIALOG_EVENT,
  dialogTabActive,
  takePendingDialog,
} from '@/lib/open-dialog';
import { circleColor } from '@/lib/circle-color';
// БУКВА ЗАГЛУШКИ — БЕЗ «@» (владелец 09.09.2026, правило П4 волны 2): у человека без
// имени именем стоит его логин, и в круге стояла собачка. Рука одна на весь кабинет.
import { askConfirm } from './ask';
import { ContactCardIos, ContactTagsSheet, contactInitial, type ContactRow } from './contacts';
import { VGroup } from './drawer-kit';
import { fullNameNote, shortName } from '@/lib/display-name';
import { msgDialogTime } from '@/lib/msg-dates';
import { useNativePhotos } from './native-photos';
import { ChannelIcon, channelHex } from './bot-templates';
import { ConfirmInline } from './confirm-inline';
import { LoadError } from './load-error';
import {
  DialogPanel,
  awaitDialogRow,
  warmDialogFeed,
  windowLeft,
  canWrite,
} from './messages-dialog';
import { rowsAfterRead, totalAfterRead } from './messages-read';
import { useIsDark } from './n8n-icon';
import { KBareButton } from '@scriptera/ui/kit';
import { IconTray } from './icons';
import {
  isAppClient,
  readLayoutMode,
  subscribeLayoutMode,
  useLayoutMode,
  readPhoneLook,
  usePhoneLook,
} from './layout-mode';
import { pathToPick, pickToPath } from '@/lib/cabinet-urls';
import { useSlidePanel } from './native-layer-host';
import { MediaViewer } from './messages-lightbox';
import { Shape, Sk, useShapeRecord } from './skeleton';
import { PaneActiveContext } from './page-action';
import { usePaneVisible } from '@/lib/pane-visible';
import { useFavorites } from '@/lib/use-favorites';
import { NATIVE_WARM_FIRST, useWarmInView } from '@/lib/warm-in-view';
import { warmTarget } from '@/lib/warm';
import { entryFolder, pickEntryDialog } from '@/lib/chats-entry';

/**
 * МЕСТО СПИСКА ПЕРЕПИСОК В КОЛОНКЕ (партия Н5, владелец 03.10.2026: «Чаты» на широком — сразу
 * разворот как в Telegram Desktop, CLAUDE.md «Фундамент дизайна» п.4–5). В трёх слоях список
 * стоит в среднем слое, а поле занимает открытая переписка. Колонку рисует оболочка
 * (MessagesSplitMenu), а список — этот раздел (у него данные, папки, режим выбора): колонка
 * объявляет свой слот сюда, раздел рисует в него ТЕ ЖЕ ряды порталом. Второго списка нет.
 */
let msgSideSlot: HTMLElement | null = null;
const msgSideSubs = new Set<() => void>();
export function setMsgSideSlot(el: HTMLElement | null): void {
  if (msgSideSlot === el) return;
  msgSideSlot = el;
  msgSideSubs.forEach((f) => f());
}
const subscribeMsgSide = (cb: () => void) => {
  msgSideSubs.add(cb);
  return () => {
    msgSideSubs.delete(cb);
  };
};
function useMsgSideSlot(): HTMLElement | null {
  return useSyncExternalStore(
    subscribeMsgSide,
    () => msgSideSlot,
    () => null,
  );
}
/** Последняя открытая в развороте переписка — её поле показывает при входе (Н5 п.2). */
const MSG_LAST_KEY = 'scr-msgs-last';
const readLastDialog = (): string => {
  try {
    return window.localStorage.getItem(MSG_LAST_KEY) ?? '';
  } catch {
    return '';
  }
};
const saveLastDialog = (id: string): void => {
  try {
    window.localStorage.setItem(MSG_LAST_KEY, id);
  } catch {
    /* хранилище закрыто — откроем первую */
  }
};

/** Диалог в списке (форма ответа /api/cabinet/messages). */
export interface DialogListRow {
  id: string;
  channel: 'tg' | 'ig';
  accountKey: string;
  name: string;
  username: string;
  /** Маленькая копия аватарки — ею рисуются все кружки. */
  avatarUrl: string;
  /** ПОЛНАЯ копия той же аватарки: её открывает тап по кружку (владелец 09.09.2026).
   *  Необязательное: у рядов, собранных из находки поиска, и у старых записей его нет —
   *  тогда просмотр открывает маленькую, а нет и её — кружок не нажимается вовсе. */
  avatarFullUrl?: string;
  lastText: string;
  lastKind: string;
  lastDirection: 'in' | 'out';
  lastAt: string;
  unread: number;
  windowUntil: string | null;
  /** Право на одно личное сообщение по свежему комментарию (09.09.2026). */
  commentReply?: { commentId: string; until: string } | null;
  takenOver: boolean;
  /** Судьба последней нашей реплики: '' · sending · failed · read. */
  lastStatus?: string;
  /** Человек заблокировал бота — писать нельзя (Телеграм). */
  peerBlocked?: boolean;
  /** Замороженный срез: аккаунт ушёл в другой кабинет — писать нельзя (ТЗ ACCOUNTS-TOGGLE §3.7). */
  frozen?: boolean;
}

export interface DialogAccount {
  channel: 'tg' | 'ig';
  key: string;
  name: string;
  /** Отключён: переписки остались, писать нельзя. */
  off?: boolean;
  /** ЛИЦО АККАУНТА (владелец 12.09.2026, П45): адрес НАШЕЙ ручки с фотографией
   *  подключения (`/api/cabinet/accounts/<credId>/avatar`, общая рука credAvatars).
   *  Поля нет — фото у аккаунта нет, ряд идёт одним значком сети. */
  avatarUrl?: string;
}

interface MessagesResp {
  ok: boolean;
  unread: number;
  accounts: DialogAccount[];
  more: boolean;
  /** Есть ли у клиента хоть одна переписка, где автоматизация выключена (в этом канале и
   *  аккаунте): пункт «Автоматизация выключена» появляется в воронке отбора только тогда
   *  (правило 4 владельца 09.09.2026). */
  hasMine?: boolean;
  dialogs: DialogListRow[];
  /** Загрузка переписок Инстаграма (О18.7): архив ещё едет / когда забирали новое. */
  sync?: SyncState | null;
}

/**
 * СОСТОЯНИЕ ЗАГРУЗКИ ПЕРЕПИСКИ — СЧИТАЕТ СЕРВЕР, ЧЕЛОВЕКУ НЕ ПОКАЗЫВАЕТСЯ НИГДЕ
 * (правило 1 владельца 11.09.2026: «убери её, это вообще не нужно больше; у нас вебхуки
 * есть, про обновление можно не писать»). Строки «Скачиваем… N», «Обновляем…» и
 * «Обновлено в 10:14» сняты целиком — и в браузере (шапка раздела, лента переписки), и в
 * приложении (вторая строка родного заголовка: веб шлёт в `setTopSubtitle` пустую
 * строку). Поле `sync` в ответе ручки остаётся: его считает сервер для своих нужд.
 * ВОЗВРАЩАТЬ СТРОКУ НА ЭКРАН ЗАПРЕЩЕНО.
 */
export interface SyncState {
  loading: boolean;
  /** Сколько переписок уже скачано (О22.3) — честное число без выдуманного знаменателя. */
  count?: number;
  at: string | null;
  /** Прямо сейчас забираем новое (список перезапрашивается). Считает сам кабинет. */
  busy?: boolean;
}

// Чистая часть поиска (форма находки, кусок вокруг слова) живёт отдельным модулем —
// её проверяют юниты; здесь только показ.
export { cutAround, rowFromHit, type MessageHit } from './messages-search';
import {
  cutAround,
  dialogMatches,
  hitMessageIdOf,
  hitPreview,
  hitRowId,
  markIn,
  previewPrefix,
  rowFromHit,
  searchPreview,
  type MessageHit,
} from './messages-search';
// Канал отбора и применимость состояний — тоже чистая часть, тоже под юнитами
// (messages-channel.ts). Наружу отдаём отсюда: колонка десктопа берёт их у раздела.
export { pickedChannel, stateFitsChannel } from './messages-channel';
import { pickedChannel, stateFitsChannel } from './messages-channel';

/** Короче двух букв не ищем: по одной букве находится вся переписка разом (сервер
 *  считает так же — SEARCH_MIN_CHARS в packages/db). */
const SEARCH_MIN = 2;

/**
 * ВОРОНКИ ОТБОРА НАД СПИСКОМ БОЛЬШЕ НЕТ (ТЗ M.3, владелец 09.09.2026). Её три пункта
 * («Личные», «Все», «Без автоматизации») стали обычными ПАПКАМИ по умолчанию, и
 * единственный отбор переписок теперь — папка: полоса капсул над списком, настройки
 * шестерёнкой. Всё про папки живёт одной рукой в messages-folders.tsx.
 */

/**
 * КУСОК НАЙДЕННОЙ РЕПЛИКИ (О22.5): вокруг слова, само слово — жирным. Реплика бывает в
 * тысячу знаков, а в ряду списка помещается строка: показываем то место, где нашлось.
 */
/** Кусок найденной реплики: слово — жирным, вокруг него контекст. */
export function Snippet({ text, needle }: { text: string; needle: string }) {
  const { before, hit, after } = cutAround(text, needle);
  if (!hit) return <>{before}</>;
  return (
    <>
      {before}
      {/* НАЙДЕННОЕ СЛОВО — ЖИРНЫМ И АКЦЕНТНЫМ (правило 1 владельца 09.09.2026): серым
          полужирным по серому его не видно вовсе. Цвет берётся классом, роль —
          --accent-stroke (нарисованное, не фон). */}
      <b className="msg-mark">{hit}</b>
      {after}
    </>
  );
}

/**
 * НАЙДЕННОЕ СЛОВО В КОРОТКОЙ СТРОКЕ — имя ряда, логин (П5 обхода владельца 11.09.2026).
 * От `Snippet` отличается тем, что ничего не обрезает: имя и логин коротки сами по себе,
 * а многоточие посреди имени читалось бы как порча. Слова в строке нет — строка как есть.
 */
export function Marked({ text, needle }: { text: string; needle: string }) {
  const m = markIn(text, needle);
  if (!m) return <>{text}</>;
  return (
    <>
      {text.slice(0, m.start)}
      <b className="msg-mark">{text.slice(m.start, m.start + m.len)}</b>
      {text.slice(m.start + m.len)}
    </>
  );
}

/** Строка поиска шапки (веб-поле и родная строка над клавиатурой) → сюда. */
export const MSG_Q_EVENT = 'scr-msg-q';
/** Аккаунты клиента списку известны раньше шапки — отдаём их ей для селекта-фильтра. */
export const MSG_ACCOUNTS_EVENT = 'scr-msg-accounts';
/** Свежее число непрочитанных → значку на вкладке (М9): список знает его раньше «Обзора». */
export const MSG_UNREAD_EVENT = 'scr-msg-unread';
/**
 * РЕЖИМ ВЫБОРА ПЕРЕПИСОК В БРАУЗЕРЕ (П13 обхода владельца 11.09.2026): «Изменить» стоит в
 * шапке раздела (её рисует shell.tsx), а кружки-галочки и кнопка «Прочитать» — в самом
 * списке. Событие несёт НОВОЕ состояние и летит в обе стороны: шапка объявляет им вход и
 * выход по «Готово», список — свой выход (сменили папку, открыли настройки, начали
 * искать). Каждая сторона просто принимает объявленное, отвечать на событие своим
 * событием нельзя — закольцуется.
 */
export const MSG_EDIT_EVENT = 'scr-msg-edit';
/** Объявить режим выбора «Чатов» включённым или выключенным (обе стороны зовут её). */
export const tellMsgEdit = (on: boolean): void => {
  window.dispatchEvent(new CustomEvent(MSG_EDIT_EVENT, { detail: { on } }));
};
/** Канал переписки → имя канала в словаре значков шаблонов (там же логотипы). */
const CHANNEL_NAME: Record<string, string> = { tg: 'telegram', ig: 'instagram' };

/** Подпись вида вложения в превью последней реплики (текст показываем как есть).
 *  Тем же словарём подписана цитата в шапке пузыря (С3) — второго не заводим. */
export const KIND_KEY: Record<string, string> = {
  image: 'kindImage',
  video: 'kindVideo',
  audio: 'kindAudio',
  voice: 'kindVoice',
  file: 'kindFile',
  sticker: 'kindSticker',
  share: 'kindShare',
  story: 'kindStory',
  // Реакция, которой не на чем висеть значком (О5): в списке читается «Реакция · 🔥» —
  // сам значок это содержимое человека, слово перед ним наше.
  reaction: 'kindReaction',
  // Показывать нечего (29.1): в строке списка стоит то же слово, что и в самой ленте —
  // «Исчезающее фото», «Сообщение удалено». Ещё не докачанное (О30) в строке списка
  // показывается нейтрально: «Загружается», а не отсутствием и не «не пришло».
  ephemeral: 'kindEphemeral',
  unsupported: 'kindUnsupported',
  deleted: 'kindDeleted',
  pending: 'kindPending',
};

/**
 * Время последней реплики по-телеграмному: сегодня — часы:минуты, вчера — «вчера»,
 * на этой неделе — день недели, дальше — дата (у чужого года — с годом, «18.08.25»).
 * Сам расчёт — в lib/msg-dates: правило года одно на весь раздел (владелец 18.08.2026).
 */
const dialogTime = msgDialogTime;

/** Подпись переписки: КОРОТКОЕ имя, иначе логин. Ни того, ни другого (профиль мессенджера
 *  ещё не подтянулся или скрыт) — честное «Без имени»: прочерк читался как пустая строка, а
 *  технический id владелец назвал мусором (2026-08-01).
 *  Укорачивание живёт в одном месте (lib/display-name.ts, паспорт §3.5): длинное имя
 *  теряет титул после разделителя, а совсем длинное подменяется логином — так делает и
 *  сам Инстаграм. Полное имя видно в карточке собеседника второй строкой. */
export function dialogTitle(d: DialogListRow): string {
  return shortName(d.name, d.username ? `@${d.username}` : '');
}

/** Аватар собеседника: фото из нашего бакета кружком 44 (`Size.avatar` словаря), а без
 *  фото — КРУГ с буквой на фирменном цвете (владелец 09.09.2026, вторая правка за день,
 *  зеркало `ScrLetterTile` приложения: «заглушка в круге — того же размера и той же
 *  формы, что аватарка»; утренний скруглённый квадрат отменён). `small` — мелкий размер
 *  для пункта второй колонки. */
/** Сколько первых рядов списка грузят фото сразу — экран телефона (≈ 10 рядов) с запасом. */
const EAGER_AVATARS = 12;

export function DialogAvatar({
  dialog,
  small = false,
  eager = false,
}: {
  dialog: DialogListRow;
  small?: boolean;
  eager?: boolean;
}) {
  const t = useTranslations('cabinet.messages');
  return (
    <PersonAvatar
      title={dialogTitle(dialog) || t('noName')}
      avatarUrl={dialog.avatarUrl}
      small={small}
      eager={eager}
    />
  );
}

/** ТОТ ЖЕ кружок человека, но по имени и ссылке — для мест, где строки не переписки, а
 *  события (журнал страницы бота, П42). Вид один: копий кружка в кабинете не заводим. */
export function PersonAvatar({
  title,
  avatarUrl,
  small = false,
  eager = false,
}: {
  title: string;
  avatarUrl: string;
  small?: boolean;
  /** Первый экран списка (пакет М10): грузить сразу, без отложенной загрузки. */
  eager?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  const size = small ? 22 : 44;
  if (avatarUrl && !broken) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- картинка человека из нашего бакета, next/image не нужен
      <img
        className={small ? 'msg-av side2-av' : 'msg-av'}
        src={avatarUrl}
        alt=""
        width={size}
        height={size}
        decoding="async"
        // Первые ряды списка — сразу (владелец 04.10.2026, пакет М10: «сначала кружки с буквами,
        // фото подъезжают по одному»): lazy Safari откладывает до раскладки. Ниже — как было.
        loading={eager ? 'eager' : 'lazy'}
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    // ЗАГЛУШКА СОБЕСЕДНИКА — КРУГ С БУКВОЙ (владелец 09.09.2026, вторая правка за день;
    // зеркало ScrLetterTile приложения: буква semibold, подложка — цвет буквы вполсилы 11 %).
    // Силуэта-картинки у собеседника не бывает нигде — ни до фото, ни вместо него. Свой
    // класс, а не общий .svc-circle разделов: у собеседников этот вид один во всех местах
    // (ряд списка, находка, карточка), а .svc-circle живёт в остальных разделах своей жизнью.
    <div className={small ? 'msg-avph side2-av' : 'msg-avph'} style={{ color: circleColor(title) }}>
      {contactInitial(title)}
    </div>
  );
}

/**
 * ЛИЦО АККАУНТА, КОТОРЫЙ ВЕДЁТ ПЕРЕПИСКУ (владелец 12.09.2026, П45): круглая аватарка
 * рядом со значком сети, РОВНО того же размера (14) — по ней видно, каким подключённым
 * аккаунтом идёт переписка. Показывается всегда, когда аккаунт известен (в отличие от
 * подписи с именем аккаунта — та появляется, только если аккаунтов несколько).
 * Фото нет или не загрузилось — не рисуем ничего: буквы-заглушки у аккаунта не бывает.
 * Адрес — всегда НАША ручка (`/api/cabinet/accounts/<credId>/avatar`, lib/cred-avatar.ts).
 */
export function AccountAvatar({ url }: { url?: string }) {
  const [broken, setBroken] = useState(false);
  if (!url || broken) return null;
  return (
    // eslint-disable-next-line @next/next/no-img-element -- фото подключения отдаёт наша же ручка, next/image не нужен
    <img
      className="msg-accav"
      src={url}
      alt=""
      width={14}
      height={14}
      decoding="async"
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
}

/** «i» в шапке переписки — ГОЛАЯ буква, без кружочка (владелец 2026-08-05: «в кружочке
 *  не нравится»). Экспорт: та же иконка в шапке переписки на широком экране. */
export const InfoIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    aria-hidden
  >
    <path d="M12 10.2v7.6" />
    <circle cx="12" cy="5.6" r="1.35" fill="currentColor" stroke="none" />
  </svg>
);

/**
 * ПОМЕТКА СОСТОЯНИЯ на строке списка — РОВНО ОДНА (владелец 2026-08-08: «чтоб не было
 * двух статусов никогда»). Считается сама, руками ничего ставить не надо. Порядок —
 * от самого важного: чем нельзя пренебречь, то и показываем.
 *
 * Отдельный случай: человек ждёт ответа, а окно уже закрыто — это не два состояния,
 * а одно и с именем: ПРОПУЩЕН ОТВЕТ.
 */
export function rowTags(
  d: DialogListRow,
  /** Ключи ЖИВЫХ аккаунтов клиента; пусто/не задано — ещё не знаем, молчим. */
  liveAccounts?: Set<string>,
): { key: string; warn?: boolean }[] {
  const waiting = d.lastDirection === 'in';
  const noAccount =
    !!liveAccounts && liveAccounts.size > 0 && !liveAccounts.has(`${d.channel}:${d.accountKey}`);
  // «Писать нельзя» — одно состояние на все причины: окно закрылось, человек заблокировал
  // бота, аккаунт отключён. Саму причину человек читает уже в переписке. Окно считается
  // ТОЙ ЖЕ меркой, что в переписке и в карточке собеседника (О23.3): срока нет вовсе —
  // значит закрыто, а не «можно».
  const cantWrite =
    noAccount || !!d.peerBlocked || !canWrite(d.channel, d.windowUntil, d.commentReply);
  // Слова — РОВНО те же, что в отборе (владелец 2026-08-08).
  if (cantWrite && waiting) return [{ key: 'tagMissed', warn: true }];
  if (cantWrite) return [{ key: 'tagWindowClosed', warn: true }];
  // ОТДЕЛЬНЫЙ СТАТУС «автоматизации выключены» (владелец 2026-08-09, IMG_1524: «должно
  // быть отдельным статусом, наравне с другими»): бот в этой переписке молчит — из
  // списка это видно, а не только внутри переписки.
  if (d.takenOver) return [{ key: 'tagMineOff' }];
  if (waiting) return [{ key: 'tagWaiting' }];
  return [];
}

/** Замок диалога переключили. Слушает тот, кто показывает состояние ОТДЕЛЬНЫМ слоем:
 *  карточка собеседника открывается со снимком содержимого и сама о переключении не
 *  узнаёт (владелец 2026-08-09, IMG_1526 — надпись оставалась прежней). */
export const MSG_MINE_EVENT = 'scr-msg-mine';
export function tellMine(id: string, on: boolean): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(MSG_MINE_EVENT, { detail: { id, on } }));
}

export function MineToggle({ on, toggle }: { on: boolean; toggle: () => void }) {
  const t = useTranslations('cabinet.messages');
  if (on) {
    return (
      <KBareButton className="msg-dlg-mine on" onClick={toggle}>
        {t('mineOn')}
      </KBareButton>
    );
  }
  return (
    <ConfirmInline
      label={t('mineOff')}
      question={t('mineAsk')}
      confirmLabel={t('mineAskYes')}
      danger={false}
      triggerClassName="msg-dlg-mine"
      onConfirm={toggle}
    />
  );
}

/** Карточка собеседника (владелец 2026-08-05): кнопка «i» справа заголовка переписки
 *  открывает нижним листом СТОПКОЙ поверх (канон NATIVE-SLIDE §11) — кто это, каким
 *  каналом и аккаунтом пришёл и, главное, сколько осталось на ответ (окно 24 ч).
 *  Верх — HERO (владелец 05.08, дозаказ): крупный аватар, имя и логин по центру;
 *  при открытии карточка СРАЗУ шлёт запрос «освежи профиль» (PATCH refreshProfile) —
 *  аватар и имя могли смениться, свежие значения ложатся на место живьём. */
export function DialogInfoSheet({
  row,
  account,
  mine,
  contact = null,
  onContactSaved,
}: {
  row: DialogListRow;
  account?: string;
  /** «Выключить / включить автоматизации» — замок диалога (широкий экран: из шапки
   *  переписки он переехал сюда). */
  mine?: { on: boolean; toggle: () => void };
  /**
   * КАРТОЧКА ЧЕЛОВЕКА ЭТОЙ ПЕРЕПИСКИ (П7 обхода владельца 11.09.2026): по ней рисуются
   * метки. null — карточки ещё нет (бот написал первым, человек только оставил
   * комментарий): первая же метка заведёт её по переписке.
   */
  contact?: ContactRow | null;
  /** Метку записали — переписку перечитать (карточка приедет вместе с ней). */
  onContactSaved?: () => void;
}) {
  const t = useTranslations('cabinet.messages');
  // Слова меток живут в «Контактах» — карточка человека одна на оба раздела.
  const tc = useTranslations('cabinet.contacts');
  // Состояние замка — ЖИВОЕ: содержимое карточки снято в момент открытия, поэтому о
  // переключении она узнаёт событием (владелец 2026-08-09, IMG_1526).
  const [mineOn, setMineOn] = useState(!!mine?.on);
  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent<{ id?: string; on?: boolean }>).detail;
      if (d?.id === row.id && typeof d.on === 'boolean') setMineOn(d.on);
    };
    window.addEventListener(MSG_MINE_EVENT, h);
    return () => window.removeEventListener(MSG_MINE_EVENT, h);
  }, [row.id]);
  // Остаток окна живой: карточку держат открытой, а цифра со временем врала бы.
  const [, setTick] = useState(0);
  const tickVisible = usePaneVisible();
  useEffect(() => {
    if (!tickVisible) return; // скрыт экран или вкладка — цифру никто не видит (ТЗ SPEED §8.1)
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [tickVisible]);
  // Живое освежение профиля при открытии (владелец 05.08).
  const [freshP, setFreshP] = useState<{
    name: string;
    username: string;
    avatarUrl: string;
    avatarFullUrl?: string;
  } | null>(null);
  useEffect(() => {
    let alive = true;
    void fetch(`/api/cabinet/messages/${row.id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshProfile: true }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (
          j: {
            ok?: boolean;
            profile?: {
              name: string;
              username: string;
              avatarUrl: string;
              avatarFullUrl?: string;
            } | null;
          } | null,
        ) => {
          if (alive && j?.ok && j.profile) setFreshP(j.profile);
        },
      )
      .catch(() => null);
    return () => {
      alive = false;
    };
  }, [row.id]);
  const shownRow: DialogListRow = freshP
    ? {
        ...row,
        name: freshP.name || row.name,
        username: freshP.username || row.username,
        avatarUrl: freshP.avatarUrl || row.avatarUrl,
        avatarFullUrl: freshP.avatarFullUrl || row.avatarFullUrl,
      }
    : row;
  const left = windowLeft(row.windowUntil, t('hoursShort'), t('minutesShort'));
  // Та же мерка, что у поля ответа переписки: одна на весь кабинет (О23.3).
  const closed = !canWrite(row.channel, row.windowUntil, row.commentReply);
  const chName = CHANNEL_NAME[row.channel] ?? row.channel;
  // ФОТО В ПОЛНЫЙ РАЗМЕР (владелец 09.09.2026): тап по аватарке открывает тот же просмотр,
  // что у кадра публикации в переписке, — одним кадром, тем же листом. Открывается ПОЛНАЯ
  // копия; её нет (записи до 09.09.2026) — открывается маленькая: кружок обязан открываться
  // тем, что есть. Пропорция задана явно 1:1 — аватарка всегда квадрат, и без неё лист брал
  // запасные 4:5 и резал картинку (разбор владельца 09.09.2026).
  const photo = shownRow.avatarFullUrl || shownRow.avatarUrl;
  const viewPanel = useSlidePanel();
  const infoPhone = usePhoneLook();
  const openPhoto = () => {
    if (!photo) return;
    viewPanel.open({
      title: t('viewer'),
      sheet: true,
      body: (
        <MediaViewer
          items={[{ kind: 'image', url: photo, width: 1, height: 1 }]}
          index={0}
          words={{
            prev: t('viewPrev'),
            next: t('viewNext'),
            openIg: t('openInInstagram'),
            download: t('viewDownload'),
          }}
        />
      ),
    });
  };
  // ТЕЛЕФОН В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 Ч27, кадр iOS C09-chat-info): карточка собеседника
  // — та же, что у человека в «Контактах» (ContactCardIos), сверху свои строки переписки:
  // окно ответа, канал, аккаунт, «Отвечает · Бот ›» (окно-вопрос «Отключить автоматизацию»).
  if (infoPhone) {
    return (
      <ContactCardIos
        contact={contact}
        dialogId={row.id}
        name={dialogTitle(shownRow) || ''}
        username={shownRow.username ?? ''}
        avatarUrl={shownRow.avatarUrl ?? ''}
        channel={row.channel}
        {...(account ? { account } : {})}
        rows={[
          {
            key: 'window',
            title: t('infoWindow'),
            value: (
              <span className={closed ? 'msg-info-closed' : undefined}>
                {row.channel === 'tg' ? t('infoWindowFree') : closed ? t('windowClosed') : left}
              </span>
            ),
          },
          ...(mine
            ? [
                {
                  key: 'answers',
                  title: t('infoAnswers'),
                  value: mineOn ? t('answerYou') : t('answerBot'),
                  chevron: true,
                  onClick: () => {
                    if (mineOn) {
                      mine.toggle();
                      return;
                    }
                    void askConfirm({
                      question: t('mineAsk'),
                      confirmLabel: t('mineAskYes'),
                      destructive: false,
                    }).then((yes) => {
                      if (yes) mine.toggle();
                    });
                  },
                },
              ]
            : []),
        ]}
        {...(row.channel === 'ig'
          ? { hint: closed ? t('windowClosedHint') : t('infoWindowHint') }
          : {})}
        {...(onContactSaved ? { onSaved: onContactSaved } : {})}
      />
    );
  }
  return (
    <div className="msg-info">
      <div className="msg-info-hero">
        {/* НЕТ АВАТАРКИ — НЕТ ТАПА (правило владельца 09.09.2026 п.4): кружок с буквой
            не кнопка, подсветки «можно открыть» у него нет. */}
        {photo ? (
          <KBareButton onClick={openPhoto} aria-label={t('viewer')}>
            <DialogAvatar dialog={shownRow} />
          </KBareButton>
        ) : (
          <DialogAvatar dialog={shownRow} />
        )}
        <div className="msg-info-who">
          <div className="ov-scn-name">{dialogTitle(shownRow) || t('noName')}</div>
          {/* ПОЛНОЕ ИМЯ — второй строкой, если оно отличается от короткого (паспорт §3.5):
              в списке титул отсечён, а здесь человека видно целиком. */}
          {fullNameNote(shownRow.name, dialogTitle(shownRow)) ? (
            <div className="meta">{fullNameNote(shownRow.name, dialogTitle(shownRow))}</div>
          ) : null}
          {shownRow.username && <div className="meta">@{shownRow.username}</div>}
        </div>
      </div>
      <div className="card msg-info-card">
        <div className="msg-info-row">
          <span className="meta">{t('infoWindow')}</span>
          <span className={`msg-info-val${closed ? ' msg-info-closed' : ''}`}>
            {row.channel === 'tg' ? t('infoWindowFree') : closed ? t('windowClosed') : left}
          </span>
        </div>
        <div className="msg-info-row">
          <span className="meta">{t('infoChannel')}</span>
          <span className="msg-info-val">
            <ChannelIcon channel={chName} size={14} />
            {chName === 'telegram' ? 'Telegram' : 'Instagram'}
          </span>
        </div>
        {account && (
          <div className="msg-info-row">
            <span className="meta">{t('infoAccount')}</span>
            <span className="msg-info-val">{account}</span>
          </div>
        )}
        {/* Замок диалога — из шапки переписки широкого экрана он переехал сюда
            (владелец 05.08: в шапке только назад · имя · «i»). Тот же словесный
            переключатель, что был в шапке. */}
        {mine && (
          <div className="msg-info-row">
            <span className="meta">{t('infoMine')}</span>
            <MineToggle on={mineOn} toggle={mine.toggle} />
          </div>
        )}
      </div>
      {row.channel === 'ig' && (
        <p className="meta msg-info-hint">{closed ? t('windowClosedHint') : t('infoWindowHint')}</p>
      )}
      {/* МЕТКИ ЧЕЛОВЕКА — ПРЯМО ИЗ ПЕРЕПИСКИ (П7 обхода владельца 11.09.2026): в
          приложении это родной лист меток, в браузере — тот же набор здесь. Карточки у
          переписки может не быть вовсе — первая метка заводит её сама. */}
      <VGroup label={tc('fldTags')}>
        <div className="msg-info-tags">
          <ContactTagsSheet contact={contact} dialogId={row.id} onSaved={onContactSaved} />
        </div>
      </VGroup>
    </div>
  );
}

/** Строка отбора для ручки списка и для ключа кэша — ОДНА на оба (порядок частей важен:
 *  ключ кэша складывается из этой же строки, и другой порядок дал бы второй кэш того же
 *  списка). Пустые части опускаются. */
/** Догруженные списки папок (ключ — папка с условиями отбора): живут, пока открыт кабинет. */
const loadedByFolder = new Map<string, DialogListRow[]>();

/** Свежая первая страница плюс запомненный хвост папки без повторов (см. `dialogsKept`). */
function withTail(key: string, fresh: DialogListRow[]): DialogListRow[] {
  const known = loadedByFolder.get(key);
  if (!known || known.length <= fresh.length) return fresh;
  const ids = new Set(fresh.map((d) => d.id));
  return [...fresh, ...known.filter((d) => !ids.has(d.id))];
}

function dialogsFlt(f: {
  channel: string;
  account: string;
  state: string;
  mine: string;
  /** Активная ПАПКА (ТЗ M.3): её условия применяет сервер; при ней kind/mine молчат. */
  folder?: string;
}): string {
  return [
    f.channel ? `channel=${encodeURIComponent(f.channel)}` : '',
    f.account ? `account=${encodeURIComponent(f.account)}` : '',
    f.state ? `state=${encodeURIComponent(f.state)}` : '',
    f.mine ? `mine=${encodeURIComponent(f.mine)}` : '',
    f.folder ? `folder=${encodeURIComponent(f.folder)}` : '',
  ]
    .filter(Boolean)
    .join('&');
}

const dialogsKey = (flt: string): string => `cabinet:messages${flt ? `:${flt}` : ''}`;
const dialogsUrl = (flt: string): string => `/api/cabinet/messages${flt ? `?${flt}` : ''}`;
const acceptDialogs = (raw: unknown): boolean => !!(raw as MessagesResp)?.ok;

// Списки других состояний текущей папки («Ждут ответа», «Непрочитанные») заранее НЕ греются: по касанию чипа их
// грузит сам экран (ТЗ ~/projects/SPEED-TZ.md §5.3 — цель `chats.states` снята; было: 4 запроса на каждое открытие папки).

/** Папок нет — ОДИН И ТОТ ЖЕ пустой массив на все вызовы: новый на каждый рендер гонял бы
 *  эффекты прогрева по кругу. */
const EMPTY_FOLDERS: string[] = [];

/**
 * Список переписок с отбором (кэш `cabinet:messages`): строка поиска приходит событием
 * шапки/колонки (MSG_Q_EVENT), хвост «Показать ещё» — местная память. С 2026-08-03
 * список живёт только в правом поле раздела (колонка — одни фильтры).
 */
export /** Фото первой страницы едут одним патчем не позже стольких мс после первого готового (15.2 п.3). */
const PHOTO_SETTLE_MS = 400;
/** Соседние страницы папок уходят нативу не раньше стольких мс после первого кадра (15.6). */
const PAGES_AFTER_OPEN_MS = 300;
const EMPTY_PHOTOS: Record<string, string> = {};

function useDialogList({
  channel,
  account,
  state = 'all',
  mine = 'all',
  folder = '',
  folders = EMPTY_FOLDERS,
}: {
  channel: string;
  account: string;
  /** Активная ПАПКА (ТЗ M.3): её условия применяет сервер. Пусто — папок нет, отбора нет. */
  folder?: string;
  /** Все папки человека по порядку полосы (id): по ним греются ПЕРВЫЕ СТРАНИЦЫ СОСЕДНИХ
   *  папок для свайпа в приложении (правило 7 владельца 11.09.2026). Пусто — греть нечего. */
  folders?: string[];
  /** Категория состояния из шапки: ждёт ответа · непрочитанные · без автоматизаций ·
   *  окно закрывается. Отбирает СЕРВЕР (в длинном списке клиентский отбор врал бы). */
  state?: string;
  /** Автоматизации: 'off' — только переписки, где отвечает человек; 'on' — где бот
   *  (владелец 2026-08-09). Отдельный отбор, сочетается с состоянием. */
  mine?: string;
}) {
  const [q, setQ] = useState('');

  // Строку поиска ведёт шапка (веб-поле и родная строка приложения) — как у «Моделей».
  useEffect(() => {
    const onQ = (e: Event) => setQ((e as CustomEvent<{ q: string }>).detail?.q ?? '');
    window.addEventListener(MSG_Q_EVENT, onQ);
    return () => window.removeEventListener(MSG_Q_EVENT, onQ);
  }, []);

  // ОТБОР — ПАПКА (ТЗ M.3, владелец 09.09.2026): её условия применяет СЕРВЕР. Прежний
  // отбор шапки (mine, состояние) никуда не делся и складывается с ней как раньше.
  const stateQ = state && state !== 'all' ? state : '';
  const mineQ = mine && mine !== 'all' ? mine : '';
  const folderQ = folder || '';
  // КАНАЛ И АККАУНТ ОТБИРАЕТ СЕРВЕР (Ф11.1, находка владельца 18.08). Раньше они резались
  // только здесь, по уже привезённой странице, — и на канале «Телеграм» получалось вот
  // что: «есть ещё» сервер считал по ОБЩЕМУ списку, автодогрузка тянула страницу за
  // страницей чужого канала, всё отсеивалось, а под единственной перепиской вечно стоял
  // ряд-заглушка. Теперь отбор уезжает на сервер целиком — «есть ещё» честное.
  const channelQ = channel && channel !== 'all' ? channel : '';
  const accountQ = account && account !== 'all' ? account : '';
  // Отборы едут одной строкой параметров — и в ключ кэша, чтобы списки разных отборов
  // не подменяли друг друга. Строку собирает ОДНА рука (dialogsFlt): её же зовёт прогрев
  // соседних отборов ниже — иначе он писал бы в кэш с другим ключом и грел впустую.
  const flt = dialogsFlt({
    channel: channelQ,
    account: accountQ,
    state: stateQ,
    mine: mineQ,
    folder: folderQ,
  });
  const {
    data: fresh,
    loading: cold,
    error,
    reload,
    mutate,
  } = usePersistentData<MessagesResp>(dialogsKey(flt), dialogsUrl(flt), { accept: acceptDialogs });

  // СМЕНА ОТБОРА НЕ ДЁРГАЕТ СТРАНИЦУ (Ф11.3, владелец 18.08: «дёрганья раздражают»).
  // У каждого отбора свой ключ кэша, и первый заход в отбор давал скелетон на месте
  // готового списка. Держим последний показанный ответ ВНУТРИ ОДНОГО канала и аккаунта:
  // чипы состояний и автоматизаций меняют только состав того же списка, и прежние ряды
  // честно стоят на месте, пока едет новый ответ. Сменили канал или аккаунт — прежние
  // переписки к новому отбору отношения не имеют (страховка passes() их всё равно
  // уберёт), там первый заход остаётся со скелетоном.
  const scope = `${channelQ}|${accountQ}`;
  const [kept, setKept] = useState<{ scope: string; resp: MessagesResp } | null>(null);
  // Правка состояния ПРЯМО В РЕНДЕРЕ — так React и советует помнить прежнее значение.
  if (fresh && kept?.resp !== fresh) setKept({ scope, resp: fresh });
  const held = kept && kept.scope === scope ? kept.resp : null;
  const data = fresh ?? held;
  const loading = cold && !held;
  // ФОРМА ЭКРАНА «Чатов» (APP-CLAIMS-1020 ред. 9, 7.4.2–7.4.3): список показан со своими данными — снять форму на
  // устройство для следующей заглушки (`<Shape name="chats"/>` ниже).
  useShapeRecord('chats', !!fresh);
  // НА ЭКРАНЕ РЯДЫ ПРЕЖНЕГО ОТБОРА (Ф13.1): свой ответ ещё едет. Пока так — списку
  // нельзя догружаться: курсор последней строки принадлежит СТАРОМУ отбору, а страница
  // приедет из НОВОГО, ляжет в хвост, и после приезда своего ответа те же переписки
  // окажутся в списке дважды (React ругается на одинаковые key). Ждём свой первый ответ.
  const holding = !fresh && !!held;

  const paneOpen = useContext(PaneActiveContext);
  // СОСЕДНИЕ ОТБОРЫ И ПАПКИ В БРАУЗЕРЕ — ПО ПЕРВОМУ КАСАНИЮ (ТЗ WEB-CAUSES М42 п.3: первый показ
  // «Чатов» обязан стоить ОДИН запрос списка; прогрев 13 папок и двух состояний уходил тем же
  // залпом, что и смена вкладки). Человек тронул экран раздела (палец на ленте/полосе папок,
  // курсор) — тогда, очередью плана прогрева, греем соседей: свайп по папкам и чипы получают
  // готовые страницы, как раньше. В приложении (свайп родным пейджером) — сразу, как было.
  const [intent, setIntent] = useState(isAppClient);
  useEffect(() => {
    if (intent || !paneOpen) return;
    const on = () => setIntent(true);
    window.addEventListener('pointerdown', on, { capture: true, passive: true });
    return () => window.removeEventListener('pointerdown', on, { capture: true });
  }, [intent, paneOpen]);
  const ready = !!data;

  /**
   * КЛЮЧ КЭША ЛЮБОЙ ПАПКИ при ТЕКУЩИХ отборах канала/аккаунта/состояния — ОДНА рука на
   * прогрев соседних папок и на чтение их из кэша. Своей строки отбора у второго места
   * заводить нельзя: другой порядок частей дал бы второй кэш того же списка.
   */
  const folderKey = useCallback(
    (id: string): string =>
      dialogsKey(
        dialogsFlt({
          channel: channelQ,
          account: accountQ,
          state: stateQ,
          mine: mineQ,
          folder: id,
        }),
      ),
    [channelQ, accountQ, stateQ, mineQ],
  );

  /**
   * ПЕРВЫЕ СТРАНИЦЫ СОСЕДНИХ ПАПОК (правило 7 владельца 11.09.2026). В приложении папки
   * листаются свайпом, и соседняя страница обязана быть не пустой ДО того, как палец её
   * дотянет. Опрос сервера по-прежнему ведёт только текущая папка — соседняя греется ОДНА
   * (следующая по полосе, у последней — предыдущая; ТЗ SPEED §5.3), один раз, очередью плана
   * прогрева (цель chats.folders), фоновую вкладку не греем, повтор — только когда сменился
   * НАБОР папок, текущая папка или отбор канала/аккаунта.
   */
  const warmedFolders = useRef<Set<string>>(new Set());
  const folderIds = folders.join(',');
  useEffect(() => {
    if (!paneOpen || !ready || !folderIds || !intent) return;
    const base = `${channelQ}|${accountQ}|${folderIds}|${folderQ}`;
    // Цель плана `chats.folders` (У2): очередь сама держит фору своему списку, скрытую вкладку и шаг (lib/warm.ts).
    if (warmedFolders.current.has(base)) return;
    warmedFolders.current.add(base);
    const ids = folderIds.split(',').filter(Boolean);
    const at = ids.indexOf(folderQ);
    const near = at < 0 ? ids[0] : (ids[at + 1] ?? ids[at - 1]); // текущую папку греет сам список
    for (const fid of near && near !== folderQ ? [near] : []) {
      const f = dialogsFlt({
        channel: channelQ,
        account: accountQ,
        state: stateQ,
        mine: mineQ,
        folder: fid,
      });
      warmTarget('chats.folders', dialogsKey(f), dialogsUrl(f), { accept: acceptDialogs });
    }
  }, [paneOpen, ready, intent, folderIds, channelQ, accountQ, stateQ, mineQ, folderQ]);

  // ЖИВОЙ СПИСОК. Первым делом — канал-пуш (lib/messages-live.ts): пришло входящее или
  // ответил бот → сервер толкает повод, и список перезапрашивается сам, за доли секунды.
  // Раздел смонтирован всегда (стек вкладок), поэтому и значок непрочитанных на вкладке
  // (MSG_UNREAD_EVENT ниже) живой из любого раздела кабинета.
  // Слушатель отдаёт ПРОМИС перечитывания (refreshPersistent = тот же путь, что reload, плюс
  // конец запроса): повод без id ждёт его конца, а не сыплет запросы поверх (ТЗ SPEED §8.4 п.2).
  const listKey = dialogsKey(flt);
  const listUrl = dialogsUrl(flt);
  const live = useMessagesLive(
    useCallback(() => refreshPersistent(listKey, listUrl, { accept: acceptDialogs }), [listKey, listUrl]),
  );

  // Запасной опрос — ТОЛЬКО когда канала нет (упал Redis, режет прокси): 30 с и только
  // пока раздел на экране в видимом документе (ТЗ SPEED §8.1). С живым каналом опроса нет:
  // то, что пишет воркер (строка об ушедшей рассылке, статус рассылки), приходит поводом
  // `messages`/`broadcasts` в тот же канал (§8.5). Прежняя подстраховка 30 с при живом канале
  // давала запросы списка на чужих экранах.
  const paneVisible = usePaneVisible();
  useEffect(() => {
    if (live || !paneVisible) return;
    const id = setInterval(() => reload(), 30_000);
    return () => clearInterval(id);
  }, [reload, live, paneVisible]);

  // Догруженные страницы («Показать ещё») живут поверх кэшированной первой (как журнал
  // действий сотрудников): кэш держит начало списка, хвост — местная память страницы.
  // ХВОСТ ПОМНИТ СВОЙ ОТБОР (Ф11.1): сменился отбор — хвост прежнего выбрасываем тут же,
  // в рендере. Без этого к новому списку снизу прилипали переписки старого, а «есть ещё»
  // считалось по чужому хвосту (эффектом это делать поздно — кадр с чужими рядами уже
  // ушёл бы на экран).
  const [tail, setTail] = useState<{ flt: string; rows: DialogListRow[]; more: boolean | null }>({
    flt,
    rows: [],
    more: null,
  });
  if (tail.flt !== flt) setTail({ flt, rows: [], more: null });
  const [busy, setBusy] = useState(false);

  const dialogs = useMemo(
    () => [...(data?.dialogs ?? []), ...(tail.flt === flt ? tail.rows : [])],
    [data, tail, flt],
  );
  const hasMore = holding ? false : ((tail.flt === flt ? tail.more : null) ?? data?.more ?? false);

  const loadMore = useCallback(async () => {
    const last = dialogs[dialogs.length - 1];
    if (!last || busy || holding) return;
    setBusy(true);
    try {
      const r = await fetch(
        `/api/cabinet/messages?before=${encodeURIComponent(last.lastAt)}${flt ? `&${flt}` : ''}`,
        {
          cache: 'no-store',
        },
      );
      const j = (await r.json()) as MessagesResp;
      // Пока страница ехала, отбор могли сменить — чужой хвост в новый список не кладём.
      if (j?.ok) {
        setTail((prev) =>
          prev.flt === flt ? { flt, rows: [...prev.rows, ...j.dialogs], more: j.more } : prev,
        );
      }
    } catch {
      /* обрыв сети — кнопка остаётся, тыкнуть можно ещё раз */
    } finally {
      setBusy(false);
    }
  }, [dialogs, busy, flt, holding]);

  /**
   * ПЕРЕПИСКУ ОТКРЫЛИ — ЕЁ РЯД ПРОЧИТАН ТУТ ЖЕ (П14 обхода владельца 11.09.2026, круг 2).
   * Раньше цифры ждали ответа сервера и следующего захода за списком: человек выходил из
   * переписки и видел её непрочитанной. Подменяем ШТАТНО — тот же кэш списка (mutate) и
   * хвост догруженных страниц; второго состояния со своими цифрами у списка нет.
   *
   * `was` — сколько сообщений было не прочитано (правду знает сама лента переписки).
   * Возвращает новый счётчик раздела (им опадает значок вкладки); null — списка в кэше
   * ещё нет, считать нечего.
   */
  const readRow = useCallback(
    (id: string, was: number): number | null => {
      setTail((prev) => {
        const rows = rowsAfterRead(prev.rows, id);
        return rows === prev.rows ? prev : { ...prev, rows };
      });
      const cur = peekPersistent<MessagesResp>(dialogsKey(flt));
      if (!cur) return null;
      const next: MessagesResp = {
        ...cur,
        dialogs: rowsAfterRead(cur.dialogs, id),
        unread: totalAfterRead(cur.unread, was),
      };
      mutate(next);
      return next.unread;
    },
    [flt, mutate],
  );

  /**
   * ПЕРЕЧИТАТЬ ПЕРВЫЕ СТРАНИЦЫ СОСЕДНИХ ПАПОК (П14). Прочитанная переписка лежит и в их
   * кэше — со старой цифрой: свайп в приложении показал бы непрочитанным то, что человек
   * только что прочёл. Текущую папку перечитывает сам список (reload), поэтому её тут
   * пропускаем. Ходим ТЕМ ЖЕ ключом и адресом, что прогрев выше (dialogsFlt) — иначе
   * писали бы во второй кэш того же списка.
   */
  const refreshFolders = useCallback(
    (ids: readonly string[]) => {
      for (const fid of ids) {
        if (!fid || fid === folderQ) continue;
        const f = dialogsFlt({
          channel: channelQ,
          account: accountQ,
          state: stateQ,
          mine: mineQ,
          folder: fid,
        });
        void refreshPersistent<MessagesResp>(dialogsKey(f), dialogsUrl(f), { accept: acceptDialogs });
      }
    },
    [channelQ, accountQ, stateQ, mineQ, folderQ],
  );

  const needle = q.trim();
  // ПОИСК ПО СОДЕРЖИМОМУ — НА СЕРВЕРЕ (О22.5, владелец 17.08: «искать по всему списку
  // сообщений, по сообщениям, а не по пользователю»). В кабинете лежит только начало
  // списка, поэтому свой отбор по нему искал бы в горстке переписок, то есть врал бы.
  // Ждём четверть секунды после последней буквы: набор слова — это не десять запросов.
  const [found, setFound] = useState<{
    q: string;
    dialogs: DialogListRow[];
    hits: MessageHit[];
  } | null>(null);
  useEffect(() => {
    // ВЫДАЧА НЕ ПРОПАДАЕТ, ПОКА В СТРОКЕ ЕСТЬ ТЕКСТ (правило 1 владельца 09.09.2026):
    // находки стираются ТОЛЬКО на пустой запрос (явная «Отмена» или очистка поля).
    // Стёртая одна буква не должна оставлять человека с пустым экраном.
    if (!needle) {
      setFound(null);
      return;
    }
    if (needle.length < SEARCH_MIN) return;
    let alive = true;
    const id = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: needle });
        if (channel !== 'all') params.set('channel', channel);
        if (account !== 'all') params.set('account', account);
        const r = await fetch(`/api/cabinet/messages/search?${params}`, { cache: 'no-store' });
        const j = (await r.json()) as {
          ok?: boolean;
          dialogs?: DialogListRow[];
          hits?: MessageHit[];
        };
        if (alive && j?.ok) setFound({ q: needle, dialogs: j.dialogs ?? [], hits: j.hits ?? [] });
      } catch {
        /* обрыв сети — прежние находки остаются на экране */
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [needle, channel, account]);

  // СТРАХОВКА, а не сам отбор (Ф11.1): канал и аккаунт режет сервер. Здесь они остаются
  // на случай, когда на экране ещё стоят ряды прежнего отбора (см. kept выше) или находки
  // поиска, — чтобы в списке не мелькнуло чужое.
  const passes = (d: DialogListRow): boolean => {
    if (channel !== 'all' && d.channel !== channel) return false;
    if (account !== 'all' && d.accountKey !== account) return false;
    return true;
  };
  // ИЩЕМ — СНАЧАЛА СВОИМИ СИЛАМИ, ПОТОМ СЕРВЕРОМ (правило 1 владельца 09.09.2026:
  // «пока набираешь — мгновенный отбор по уже загруженным рядам, серверные находки
  // добавляются следом»). Серверная выдача считается своей, только когда пришла РОВНО
  // на набранное слово: ответ на прежнее слово подменял бы список чужими рядами.
  const searching = needle.length >= SEARCH_MIN;
  const answered = searching && found?.q === needle;
  // РЯД БЕЗ СОВПАДЕНИЯ В ВЫДАЧЕ НЕ ПОКАЗЫВАЕМ (П5 обхода владельца 11.09.2026): человеку
  // нечего в нём увидеть — ни в имени, ни в логине, ни в последней реплике слова нет.
  // Сервер такие и не отдаёт (queryPeopleOnly ищет по имени и логину), но мерка на
  // экране одна на оба пути отбора — своими силами и по ответу сервера.
  // ДОГРУЖЕННОЕ НЕ ПРОПАДАЕТ ПРИ СМЕНЕ ПАПКИ (владелец 16.09.2026, сборки 264–269: «каждая
  // смена папки открывает сверху»). На смене папки список собирается заново с первой
  // страницы (50), хвост догрузки обнуляется — страница из 150 рядов сжималась, прокрутка
  // уезжала наверх, плашка вставала на место и стекло гасло. Папка помнит всё, что в ней
  // было догружено (ключ — папка с условиями отбора): на экран идёт СВЕЖАЯ первая страница
  // плюс запомненный хвост без повторов; хвост сам заменится, когда догрузка его догонит.
  // Находки поиска сюда не пишутся: это не список папки.
  const dialogsKept = needle ? dialogs : withTail(folderKey(folder), dialogs);
  if (!needle && dialogsKept.length > 0) loadedByFolder.set(folderKey(folder), dialogsKept);
  const shown = (
    searching
      ? answered
        ? (found?.dialogs ?? []).filter((d) => dialogMatches(d, needle))
        : dialogs.filter((d) => dialogMatches(d, needle))
      : dialogsKept
  ).filter(passes);
  // Реплики внутри переписок знает только сервер — до его ответа находок нет.
  const hits = (answered ? (found?.hits ?? []) : []).filter((h) => passes(rowFromHit(h)));

  /**
   * ПЕРЕПИСКА ВЫЕЗЖАЕТ УЖЕ С СООБЩЕНИЯМИ (П39 обхода владельца 12.09.2026). Лента
   * грузилась после нажатия, и человек всю анимацию выезда смотрел на пустой экран.
   * Греем её страницу заранее — ТЕМ ЖЕ ключом и адресом, что читает сама лента
   * (warmDialogFeed, messages-dialog.tsx): второго кэша переписки в кабинете нет.
   *
   * Каждую переписку греем ОДИН раз за заход (память ref): список живой, ряды в нём
   * переставляются от каждого входящего, и без этой памяти прогрев ходил бы за теми же
   * страницами по кругу.
   */
  const warmedFeeds = useRef<Set<string>>(new Set());
  // `urgent` — касание/наведение ряда: мимо очереди фона, сразу (PREFETCH-TZ §2.2).
  // Фон — ЛЁГКАЯ лента (20 последних, PREFETCH-CAREFUL §2 п.5): экран откроется из кэша тем же кадром, а штатное
  // обновление после открытия принесёт полную страницу. Касание — сразу полная.
  const warmFeed = useCallback((id: string, urgent = false) => {
    if (!id) return;
    if (warmedFeeds.current.has(id)) {
      // Уже заказана фоном — касание лишь торопит её (полной лентой), если она ещё ждёт в очереди.
      if (urgent) warmDialogFeed(id, { urgent: true });
      return;
    }
    warmedFeeds.current.add(id);
    warmDialogFeed(id, urgent ? { urgent: true } : { light: true });
  }, []);

  // ЧТО ГРЕТЬ ИЗ СПИСКА, РЕШАЕТ РАЗДЕЛ (PREFETCH-CAREFUL-TZ §2 п.1, владелец 11.10.2026: «аккуратный прогрев, чатов
  // миллионы»): только видимые ряды и полэкрана вперёд (`useWarmInView` в MessagesSection), а не весь список папки;
  // находки поиска — тем же правилом. Прежний прогрев «все переписки списка» (PREFETCH-TZ §3 п.1) отменён.

  // ПРИЗНАКА «ИДЁМ ЗА НОВЫМ» НАРУЖУ БОЛЬШЕ НЕТ (правило 1 владельца 11.09.2026): строку
  // состояния сняли везде, и показывать его стало нечем.
  return {
    data,
    dialogs,
    shown,
    hits,
    /** Что ищем прямо сейчас (пусто — не ищем): по нему кабинет подсвечивает найденное. */
    needle: needle.length >= SEARCH_MIN ? needle : '',
    /**
     * СЛОВО НАБРАНО, А ОТВЕТ СЕРВЕРА НА НЕГО ЕЩЁ НЕ ПРИШЁЛ (владелец 10.09.2026, второй
     * обход, видео 11:37:51 со словом «Отно»). Свои силы видят только загруженные ряды, и
     * на этой секунде список пуст — но «ничего не попало» тут ЛОЖЬ: находки приезжают
     * через мгновение. Пока флаг стоит, пустой список молчит: слова о пустоте говорим
     * только после ответа сервера.
     */
    searchPending: searching && !answered,
    /** НАБРАННОЕ КАК ЕСТЬ — его показывает плашка поиска родного списка (Н5 фикслиста):
     *  человеку видно свой запрос и до порога, с которого мы начинаем искать. */
    query: q,
    hasMore,
    loadMore,
    busy,
    loading,
    hasMine: !!data?.hasMine,
    /** Ключ кэша списка ЛЮБОЙ папки при текущих отборах — им раздел читает первые страницы
     *  соседних папок для свайпа в приложении (правило 7 владельца 11.09.2026). */
    folderKey,
    /** Переписку открыли: её ряд прочитан тут же (П14). Отдаёт новый счётчик раздела. */
    readRow,
    /** Сервер подтвердил прочтение: перечитать первые страницы соседних папок (П14). */
    refreshFolders,
    /** Прогреть ленту переписки, пока её не открыли (П39): нажатие на ряд, наведение
     *  мыши, тап-вниз в приложении. Одна переписка греется один раз за заход. */
    warmFeed,
    error,
    reload,
  };
}

/**
 * ПОСЛЕДНИЙ ИЗВЕСТНЫЙ СПИСОК АККАУНТОВ (Ф13.2). Аккаунты знает лента раздела, а нужны они
 * и второй колонке — она получает их событием MSG_ACCOUNTS_EVENT. Событие уходит из
 * эффекта ленты, а эффекты детей случаются РАНЬШЕ эффектов оболочки: на первом кадре с
 * тёплым кэшем колонка подписаться ещё не успевала и оставалась без аккаунтов совсем.
 * Поэтому список лежит ещё и здесь — оболочка забирает его сама, когда подписывается.
 */
let lastAccounts: DialogAccount[] = [];
export const knownAccounts = (): DialogAccount[] => lastAccounts;

/**
 * «НЕ УДАЛОСЬ ОБНОВИТЬ» (Ф13.4, находка ревью 18.08). Список на экране может быть НЕ
 * свежим: связь оборвалась, а ряды остались — прежнего отбора или прежней минуты. Молчать
 * об этом нельзя: человек считает список правдой и ждёт в нём новых сообщений. Говорим
 * ровно то, что случилось, и даём повторить; про коды, ручки и сервер — ни слова
 * (запрет №7: наша кухня человеку не показывается).
 */
function RefreshFailed({ retry }: { retry: () => void }) {
  const t = useTranslations('cabinet.messages');
  const tc = useTranslations('cabinet');
  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="meta" style={{ lineHeight: 1.5 }}>
        {t('refreshFailed')}
      </div>
      <KButton size="sm" style={{ marginTop: 10 }} onClick={retry}>
        {tc('retry')}
      </KButton>
    </div>
  );
}

/**
 * СТРОКА ПЕРЕПИСКИ ИЗ ОТВЕТА РУЧКИ `/api/cabinet/messages/<id>` — ОДНА рука на оба пути
 * открытия переписки извне: адрес (браузер) и просьба модуля (приложение, П10г). Второй
 * сборки строки со своими запасками не заводим: разъедутся — и переписка откроется
 * по-разному с двух дорог.
 */
function dialogRowOf(d: Partial<DialogListRow> & { id: string }): DialogListRow {
  return {
    id: d.id,
    channel: d.channel ?? 'ig',
    accountKey: d.accountKey ?? '',
    name: d.name ?? '',
    username: d.username ?? '',
    avatarUrl: d.avatarUrl ?? '',
    avatarFullUrl: d.avatarFullUrl ?? '',
    lastText: d.lastText ?? '',
    lastKind: d.lastKind ?? 'text',
    lastDirection: d.lastDirection ?? 'in',
    lastAt: d.lastAt ?? new Date().toISOString(),
    unread: d.unread ?? 0,
    windowUntil: d.windowUntil ?? null,
    commentReply: d.commentReply ?? null,
    takenOver: d.takenOver ?? false,
    peerBlocked: d.peerBlocked ?? false,
  };
}

/** ПУСТОЙ СПИСОК. На телефоне в браузере — лоток 56 и слово 20 серым по центру экрана
 *  (кадры iOS C01-chats-folder-empty / K13-contacts, ТЗ WEB-PARITY-3 Ч5/К30 — тот же
 *  KEmpty, что у пустого поиска «Ботов», Б6); на широком экране — строка 15 под плашкой. */
export function ListEmptyNote({ text }: { text: string }) {
  const phone = readPhoneLook();
  return phone ? (
    <KEmpty className="scn-empty-note list-empty-ios" icon={<IconTray />} title={text} />
  ) : (
    <p className="list-empty">{text}</p>
  );
}

export function MessagesSection({
  channel,
  account,
  state = 'all',
  mine = 'all',
  pick = null,
  onPick,
  onState,
}: {
  /** Фильтр канала ('all' — оба): на широком экране — из куска пути (/app/messages/tg|ig),
   *  на узком — из селекта шапки (?channel=). */
  channel: string;
  /** Фильтр аккаунта ('all' — все боты и аккаунты клиента). */
  account: string;
  /** Категория состояния из шапки раздела (считается сама, см. useDialogList). */
  state?: string;
  /** Отбор по автоматизациям из шапки раздела: all · on · off. */
  mine?: string;
  /** Адресный выбор второй колонки: null — колонки нет (вид прежний, список и шторка),
   *  '' или `ch:<канал>` — список отбора, иначе id открытой переписки. */
  pick?: string | null;
  onPick?: (p: string) => void;
  /** Смена состояния из ЧИПОВ над списком/чатом (владелец 11.08.2026): тот же отбор,
   *  что в селекте шапки, только виден всегда. */
  onState?: (v: string) => void;
}) {
  const t = useTranslations('cabinet.messages');
  const tRoot = useTranslations('cabinet');
  const tf = useTranslations('cabinet.messages.folders');
  const layoutMode = useLayoutMode();
  const locale = useLocale();
  const panel = useSlidePanel();
  /** Переписка, открытая РОДНЫМ экраном приложения (трек Н): веб-слоя у неё нет. Вместе
   *  с ней — НАЙДЕННАЯ РЕПЛИКА (П5 обхода владельца 11.09.2026): родная лента встаёт
   *  ровно на неё, как веб-шторка. Пусто — открыли обычным концом, из списка. */
  const [nativeDialog, setNativeDialog] = useState<{ d: DialogListRow; at: string } | null>(null);
  // ПЕРЕПИСКА ПО АДРЕСУ (владелец 09.09.2026: пуш о сообщении ведёт в саму переписку).
  // На широком экране id из адреса ведёт вторую колонку (pick); на узком и в приложении
  // адрес раньше ничем не заканчивался — список и всё. Теперь id из адреса открывает
  // переписку тем же путём, что тап по ряду: строку берём из списка, а если её там ещё
  // нет — из ручки переписки. Адрес после этого возвращается к списку, чтобы возврат
  // назад не открывал её снова.
  const pathname = usePathname();
  const router = useRouter();
  const openedFromPathRef = useRef('');
  /** Переписку открыли из поиска НА РЕПЛИКЕ (О22.5) — на широком экране это состояние
   *  правого поля: адрес несёт только переписку, а место в ней живёт здесь. */
  const [atMsg, setAtMsg] = useState<{ dialog: string; message: string } | null>(null);
  // ПАПКИ (ТЗ M.3): единственный отбор переписок. Активная папка живёт на устройстве,
  // условия применяет сервер, а настройки открывает шестерёнка полосы — в браузере
  // веб-шторкой, в приложении родной полушторкой (feature-detect внутри руки).
  // Полосе — только видимые папки (П156 п.2): пустая «только с перепиской» в ней не стоит.
  const {
    shown: folders,
    current: folder,
    setCurrent: setFolder,
    reload: reloadFolders,
    readDialog: readFolderDialog,
  } = useDialogFolders();
  /** У активной папки вправду есть условия — только тогда пустой список зовётся отбором. */
  const folderHasRules = Object.keys(folders.find((f) => f.id === folder)?.rules ?? {}).length > 0;
  /** Порядок папок полосы — им же идут страницы пейджера в приложении (правило 6). */
  const folderIds = useMemo(() => folders.map((f) => f.id), [folders]);
  const {
    data,
    dialogs,
    shown,
    hits,
    needle,
    searchPending,
    query: msgQuery,
    hasMore,
    loadMore,
    busy,
    loading,
    error,
    reload,
    folderKey,
    readRow,
    refreshFolders,
    warmFeed,
  } = useDialogList({
    channel,
    account,
    state,
    mine,
    folder,
    folders: folderIds,
  });

  /**
   * ОТКРЫТУЮ ПЕРЕПИСКУ СЧИТАЕМ ПРОЧИТАННОЙ СРАЗУ (П14 обхода владельца 11.09.2026, круг 2:
   * «открыл переписку — а в списке она всё ещё непрочитанная»). Сервер о прочтении узнаёт
   * тем же PATCH, что и раньше (его шлёт сама лента), а цифры на экране опадают, не
   * дожидаясь ответа: ряд списка, наклейка активной папки и значок вкладки.
   *
   * `was` — сколько сообщений было не прочитано: его знает лента переписки, и только она
   * (ряд списка мог приехать снимком минуту назад).
   */
  const readNow = (id: string, was: number) => {
    const total = readRow(id, was);
    readFolderDialog(folder, was);
    // Значок вкладки живёт на событии (М9). Обычно его шлёт эффект ниже — он видит
    // подменённые данные; но переписки может не быть в кэше первой страницы (она в хвосте
    // догрузки), и тогда единственный, кто знает новое число, — вот эта рука.
    if (typeof total === 'number') {
      window.dispatchEvent(new CustomEvent(MSG_UNREAD_EVENT, { detail: { unread: total } }));
    }
  };

  /** Сервер прочтение подтвердил: перечитываем список, папки и первые страницы соседних
   *  папок — дальше на экране стоят цифры сервера, а не наша оптимистика (П14). */
  const readDone = () => {
    reload();
    reloadFolders();
    refreshFolders(folderIds);
  };

  /**
   * РЕЖИМ ВЫБОРА ПЕРЕПИСОК В БРАУЗЕРЕ (П13 обхода владельца 11.09.2026, паритет веб =
   * приложение). В приложении режим ведёт натив (см. `nativeEdit` ниже — оттуда ему едут
   * только слова), а в браузере его не было вовсе: «Изменить» не было ни в шапке, ни в
   * списке. Теперь режим тот же: «Изменить» в шапке раздела (рисует shell.tsx, ведёт
   * событием MSG_EDIT_EVENT), кружки-галочки у переписок и ОДНА кнопка внизу —
   * «Прочитать (N)» / «Прочитать все». Удаления в режиме нет, как и в приложении.
   * На широком экране режима не бывает: там вторая колонка, и список из неё не читают.
   */
  const [editing, setEditing] = useState(false);
  const [marked, setMarked] = useState<string[]>([]);
  /** Выйти из режима и СКАЗАТЬ об этом шапке (её кнопка снова зовётся «Изменить»). */
  const leaveEdit = useCallback(() => {
    setEditing(false);
    setMarked([]);
    tellMsgEdit(false);
  }, []);
  useEffect(() => {
    const h = (e: Event) => {
      const on = !!(e as CustomEvent<{ on?: boolean }>).detail?.on;
      setEditing(on);
      if (!on) setMarked([]);
    };
    window.addEventListener(MSG_EDIT_EVENT, h);
    return () => window.removeEventListener(MSG_EDIT_EVENT, h);
  }, []);
  // НАЧАЛИ ИСКАТЬ — РЕЖИМ СНЯТ (как в приложении: при поиске кнопки «Изменить» нет
  // вовсе). «Все» в выдаче означало бы не то, что на экране.
  useEffect(() => {
    if (needle && editing) leaveEdit();
  }, [needle, editing, leaveEdit]);
  /** Смена папки, шестерёнка и прочее действие полосы: сперва выходим из режима, потом
   *  само действие (правило владельца по режиму «Изменить»). */
  const outOfEdit = (act: () => void) => {
    if (editing) leaveEdit();
    act();
  };
  // ПАПКИ ЛИСТАЮТСЯ ХОДОМ (ТЗ ANIM-FIX О5 П6, folder-pager.tsx): тап по папке и свайп по
  // списку — горизонтальные страницы, как Telegram iOS; отбор — прежний (setFolder).
  const msgPager = useFolderPager(folderIds, folder, (id) => outOfEdit(() => setFolder(id)));
  const markedSet = new Set(marked);
  const toggleMark = (id: string) => {
    setMarked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Канал, по которому идёт отбор (Ф11.2): выбранный канал или канал выбранного аккаунта.
  const chOfList = pickedChannel(channel, account, data?.accounts);

  // Аккаунты для селекта-фильтра шапки: отдаём их ей, как только пришли.
  const sentRef = useRef('');
  useEffect(() => {
    const accs = data?.accounts;
    if (!accs) return;
    const stamp = JSON.stringify(accs);
    if (sentRef.current === stamp) return;
    sentRef.current = stamp;
    lastAccounts = accs;
    window.dispatchEvent(new CustomEvent(MSG_ACCOUNTS_EVENT, { detail: { accounts: accs } }));
  }, [data]);

  // Значок непрочитанных на вкладке (М9): список знает свежее число раньше «Обзора»,
  // из которого значок берёт начальное значение — открыли переписку, значок опал сразу.
  useEffect(() => {
    if (typeof data?.unread !== 'number') return;
    window.dispatchEvent(new CustomEvent(MSG_UNREAD_EVENT, { detail: { unread: data.unread } }));
  }, [data]);

  // СТРОКИ СОСТОЯНИЯ У СПИСКА БОЛЬШЕ НЕТ (правило 1 владельца 11.09.2026): ни «Обновляем…»,
  // ни «Скачиваем… N» — ни в шапке раздела, ни второй строкой родного заголовка. Событие
  // MSG_SYNC_EVENT снято вместе с показом: слушать было бы нечего.

  // ДОГРУЗКА САМА (О22.4): конец списка подъехал к экрану — просим следующую страницу.
  // Наблюдатель пересобирается, когда меняется сама работа догрузки или признак «есть
  // ещё»: иначе он держал бы устаревший курсор последней строки.
  const moreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = moreRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadMore();
      },
      // Пол-экрана запаса: страница едет к нам, пока человек ещё листает видимое.
      { rootMargin: '400px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadMore]);

  // Из какого отбора открыли переписку — сюда ведёт кнопка «Назад» (как открытый
  // скрипт в «Аналитике»: лента живёт НА МЕСТЕ списка, владелец 2026-08-03).
  const lastGroup = useRef('');
  useEffect(() => {
    if (pick === '' || pick?.startsWith('ch:')) lastGroup.current = pick;
  }, [pick]);

  // ГОЛОГО СПИСКА НА ШИРОКОМ ЭКРАНЕ НЕ БЫВАЕТ (владелец 11.08.2026: «должно открываться
  // каким-то сообщением, а не списком контактов»). Делаем это ТОЛЬКО в развороте трёх слоёв,
  // где список виден рядом, в колонке (Н5, 03.10.2026; прежний порог 1200 снят) — в двух
  // слоях человек остался бы в переписке без пути к списку. Сам эффект — ниже (autoOpened).
  // ОКНО РАСШИРИЛИ, А ПЕРЕПИСКА ОТКРЫТА ШТОРКОЙ (владелец 11.08.2026): на узком экране
  // чат живёт слоем поверх списка, на широком — правым полем. Без уборки после
  // расширения на экране оказывались ДВА чата разом — слой и разворот. Снимаем слой и
  // открываем ту же переписку в развороте, чтобы человек остался там же, где был.
  // ПАНЕЛЬ РАЗДЕЛА ЖИВЁТ, ДАЖЕ КОГДА ОН НЕ ОТКРЫТ (стек вкладок держит её смонтированной):
  // без этой проверки автооткрытие переписки уводило на «Сообщения» с любой другой
  // страницы кабинета (поймано пробой 11.08.2026 на «Аналитике»).
  const paneActive = useContext(PaneActiveContext);
  // SPEED 5.6/8.2: прогрев строк — только у видимой вкладки при видимом документе (pane-visible.ts, О4).
  const paneVisible = usePaneVisible();
  const sheetDialogRef = useRef('');
  useEffect(() => {
    // Порог и подписка — из одного источника раскладки (layout-mode.ts, ТЗ WEB-LOOK Э0.2).
    const onChange = () => {
      // Переписка в двух и трёх слоях живёт в поле (ТЗ WEB-LOOK Э2.3), листом — на телефоне.
      if (!paneActive || readLayoutMode() === 'phone' || !sheetDialogRef.current) return;
      const id = sheetDialogRef.current;
      sheetDialogRef.current = '';
      // Переписка переезжает из листа в поле — лист снимается целиком, со всем, что над ним.
      panel.closeAll();
      onPick?.(id);
    };
    return subscribeLayoutMode(onChange);
  }, [panel, onPick, paneActive]);

  // РАЗВОРОТ КАК TELEGRAM DESKTOP (партия Н5, владелец 03.10.2026): в трёх слоях список
  // переписок стоит в КОЛОНКЕ (слот объявляет MessagesSplitMenu), поле — открытая переписка.
  // Тогда «Назад» в шапке переписки не нужен — список и так виден. В двух слоях колонки нет:
  // список в поле, переписка открывается на его месте с «‹» (как телефон).
  const sideSlot = useMsgSideSlot();
  const beside = layoutMode === 'three' && pick !== null && !!sideSlot;

  // ГОЛОГО СПИСКА НА ШИРОКОМ ЭКРАНЕ НЕ БЫВАЕТ (Н5 п.2; пакет Д1.3, владелец 04.10.2026, Закон 5
  // п.5): при входе в раздел на двух и трёх слоях поле сразу показывает переписку — последнюю
  // ОТКРЫТУЮ человеком (память на сервере: личный набор `lastDialog`, браузер — кэш до ответа),
  // а нет её в списке — самую свежую; пустая запомненная папка на входе → «Все». Правило —
  // lib/chats-entry.ts (тест рядом). Телефон (pick === null) — список, как был.
  // Три слоя: поле — разворот, ведём и при смене папки (ключ «папка|переписка»). Два слоя:
  // переписка встаёт на место списка — только ОДИН раз на вход в раздел, иначе «‹» к списку
  // тут же открывал бы её снова.
  const { favs: lastFav, setList: setLastFav } = useFavorites('lastDialog');
  const lastServer = [...lastFav][0] ?? '';
  const autoOpened = useRef('');
  const entryDone = useRef(false);
  useEffect(() => {
    if (!paneActive) entryDone.current = false;
  }, [paneActive]);
  useEffect(() => {
    if (!paneActive) return; // раздел не открыт — не уводить человека сюда
    // ТЕЛЕФОН (владелец 04.10.2026, пакет М9: «папка Telegram запомнена и пуста — „Ничего не
    // найдено“, пришлось тапать „Все“»): то же правило папки входа, что на широком, — пустая
    // запомненная папка на входе → «Все». Переписку на телефоне при входе не открываем (список и
    // есть страница). Один раз на вход в раздел; папку, выбранную внутри раздела, не подменяем.
    if (pick === null) {
      if (entryDone.current || !data || loading || needle || folders.length === 0) return;
      entryDone.current = true;
      if (shown.length === 0) {
        const nf = entryFolder(folders, folder, true);
        if (nf !== folder) setFolder(nf);
      }
      return;
    }
    if (!onPick) return; // колонки нет — список и есть страница
    if (pick && !pick.startsWith('ch:')) {
      entryDone.current = true;
      // Запомнить открытую: кэш браузера сразу, сервер — когда переписка сменилась.
      saveLastDialog(pick);
      if (lastServer !== pick) setLastFav([pick]);
      return; // переписка уже открыта
    }
    if (!beside && entryDone.current) return;
    if (!data || loading || needle) return;
    if (shown.length === 0) {
      // Пустая запомненная папка — НА ВХОДЕ встаём на «Все» (папку без условий). Папку, которую
      // человек выбрал сам уже внутри раздела, не подменяем.
      if (!entryDone.current) {
        const nf = entryFolder(folders, folder, true);
        if (nf !== folder) setFolder(nf);
      }
      return;
    }
    const id = pickEntryDialog(shown, lastServer || readLastDialog());
    if (!id) return;
    const key = `${pick}|${id}`;
    if (autoOpened.current === key) return;
    autoOpened.current = key;
    entryDone.current = true;
    onPick(id);
  });

  // ОТБОР, КОТОРОГО У КАНАЛА НЕ БЫВАЕТ, СНИМАЕМ (Ф11.2). Обычным путём в него не попасть
  // (смена канала и аккаунта сбрасывает состояние), но по старой ссылке
  // /app/messages/ch:tg?state=missed человек оказался бы в вечно пустом списке, из
  // которого нечем выйти: чипа этого состояния на экране уже нет.
  // Рука снятия живёт в ref: у оболочки она пересоздаётся на каждый рендер, и эффект
  // с ней в зависимостях перезапускался бы столько же раз (Ф13.7).
  const stateRef = useRef(onState);
  useEffect(() => {
    stateRef.current = onState;
  });
  useEffect(() => {
    if (!paneActive || !stateRef.current) return;
    if (!stateFitsChannel(state || 'all', chOfList)) stateRef.current('all');
  }, [paneActive, state, chOfList]);

  // Живые аккаунты клиента (ответ списка их уже несёт): по ним видно переписки, у которых
  // аккаунт отключён — там ни бот не ответит, ни человек не напишет (заказ владельца 08.08).
  const liveAccounts = new Set(
    (data?.accounts ?? []).filter((a) => !a.off).map((a) => `${a.channel}:${a.key}`),
  );

  // К КАКОМУ АККАУНТУ относится переписка (владелец 31.08.2026): значка канала мало —
  // подключённых аккаунтов у клиента бывает несколько. Имя показываем ТОЛЬКО когда их
  // больше одного: при единственном это шум в и без того тесной строке.
  const accs = data?.accounts ?? [];
  const showAcc = accs.length > 1;
  /** Аккаунт переписки — одной рукой на все места ряда (имя, лицо): второго поиска по
   *  своду аккаунтов не заводим. */
  const accOf = (channel: string, key: string) =>
    accs.find((a) => a.channel === channel && a.key === key);

  // ОТКРЫТИЕ ПЕРЕПИСКИ — ОДНО НА СПИСОК, НА ПОИСК И НА РОДНОЙ СПИСОК (О22.5,
  // NATIVE-TABS): из находки переписка открывается ТЕМ ЖЕ путём, только с указанием
  // реплики, на которой надо встать; тап по ряду родной вкладки «Чаты» зовёт эту же руку.
  const openDialogRef = useRef<(d: DialogListRow, at?: string) => void>(() => undefined);
  useEffect(() => {
    if (pick !== null) return; // широкий экран: адресом правит вторая колонка
    // АДРЕС КАБИНЕТА — БЕЗ ЯЗЫКА (переезд 10.09.2026): /app/messages/<id>. Прежний разбор
    // ждал /ru/… и молча не открывал переписку — «Открыть переписку» из карточки человека
    // не работала (претензия владельца 10.09, п.12б). Старый вид с языком читаем тоже.
    const m = /^\/(?:(?:ru|en)\/)?app\/messages\/([0-9a-f-]{36})(?:\?|$)/i.exec(pathname ?? '');
    const id = m?.[1] ?? '';
    if (!id || openedFromPathRef.current === id) return;
    openedFromPathRef.current = id;
    let alive = true;
    const known = dialogs.find((d) => d.id === id);
    const go = (d: DialogListRow) => {
      if (!alive) return;
      openDialogRef.current(d);
      router.replace('/app/messages');
    };
    if (known) {
      go(known);
    } else {
      // Строки в списке нет — берём её ЧЕРЕЗ КЭШ ленты (PREFETCH-TZ §2.2): та же ручка, и лента
      // переписки ляжет в кэш тем же ответом — открывается уже с сообщениями.
      void awaitDialogRow(id).then((row) => {
        if (row) go(dialogRowOf(row));
      });
    }
    return () => {
      alive = false;
    };
  }, [pathname, pick, dialogs, locale, router]);

  /**
   * ПЕРЕПИСКА ПО ПРОСЬБЕ МОДУЛЯ (владелец 11.09.2026, П10г): из «Контактов» в приложении
   * переписку открывает не адрес, а одноразовая просьба (lib/open-dialog.ts) — адресу
   * остаётся привести человека на вкладку «Чаты». Раздел смонтирован — берём просьбу
   * событием; не смонтирован — заберём вот этим же эффектом при монтировании.
   *
   * Строку переписки берём ТАМ ЖЕ, где адресный путь: в готовом списке, а нет её там —
   * у ручки переписки. Рефы вместо зависимостей нарочно: список живой (опрос, смена
   * папки), и эффект с ним в зависимостях пересобирался бы посреди запроса — ровно от
   * этого адресный путь и не доезжал в приложении.
   */
  // Список — рефом (как рука снятия отбора выше): у оболочки он пересобирается каждый
  // рендер, и эффект с ним в зависимостях перезапускался бы столько же раз.
  const dialogsRef = useRef(dialogs);
  useEffect(() => {
    dialogsRef.current = dialogs;
  });
  /** Просьба, взятая до смены вкладки: ждёт объявления оболочки (П19). Рефом, а не
   *  местной переменной эффекта: эффект пересобирается (сменился вид раздела), а просьба
   *  человека от этого пропадать не должна. */
  const heldAskRef = useRef<{ id: string; at: string } | null>(null);
  useEffect(() => {
    if (pick !== null) return; // широкий экран: переписку ведёт вторая колонка
    let alive = true;
    let frame = 0;
    /** Поднять переписку: строку берём в готовом списке, нет её там — у ручки переписки. */
    const show = (want: { id: string; at: string }) => {
      if (!alive) return;
      const known = dialogsRef.current.find((d) => d.id === want.id);
      if (known) {
        openDialogRef.current(known, want.at);
        return;
      }
      // Через кэш ленты (PREFETCH-TZ §2.2), как адресный путь выше.
      void awaitDialogRow(want.id).then((row) => {
        if (!alive) return;
        if (!row) {
          journalLog('msgs.openAskFail', {});
          return;
        }
        openDialogRef.current(dialogRowOf(row), want.at);
      });
    };
    /** Кадр форы после смены вкладки: родной экран переписки рождается уже во вкладке
     *  «Чаты», а не поверх уезжающей прежней. */
    const soon = (want: { id: string; at: string }) => {
      frame = window.requestAnimationFrame(() => show(want));
    };
    const take = () => {
      const want = takePendingDialog();
      if (!want) return;
      // ПРОСЬБУ ИСПОЛНЯЕМ ТОЛЬКО НА АКТИВНОЙ ВКЛАДКЕ «ЧАТЫ» (П19 обхода владельца
      // 11.09.2026, круг 2). По журналу 11.09 `chatOpen` уходил за ~200 мс до
      // `setActiveTab`, и родной экран чата рождался во вкладке «Аккаунт» — открывался
      // поверх неё и пропадал вместе с ней. Вкладка ещё не наша — просьбу держим до
      // объявления оболочки (DIALOG_TAB_EVENT), а не бросаем.
      const waitTab = !dialogTabActive();
      journalLog('msgs.openAsk', { at: want.at ? 1 : 0, waitTab: waitTab ? 1 : 0 });
      if (waitTab) {
        heldAskRef.current = want;
        return;
      }
      soon(want);
    };
    /** Вкладка «Чаты» стала активной — отпускаем отложенную просьбу. */
    const onTab = () => {
      const want = heldAskRef.current;
      heldAskRef.current = null;
      if (want) soon(want);
    };
    take();
    // Просьбу мог отложить прежний вид раздела (эффект пересобрался на смене вида), а
    // вкладка тем временем уже стала нашей — тогда исполняем её сразу.
    if (dialogTabActive()) onTab();
    window.addEventListener(OPEN_DIALOG_EVENT, take);
    window.addEventListener(DIALOG_TAB_EVENT, onTab);
    return () => {
      alive = false;
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener(OPEN_DIALOG_EVENT, take);
      window.removeEventListener(DIALOG_TAB_EVENT, onTab);
    };
  }, [pick]);

  const openDialog = (d: DialogListRow, at = '') => {
    if (pick !== null) {
      setAtMsg(at ? { dialog: d.id, message: at } : null);
      onPick?.(d.id);
      return;
    }
    // Запоминаем, что переписка открыта ШТОРКОЙ узкого вида: расширят окно —
    // её надо снять (эффект ниже), иначе на экране два чата разом.
    sheetDialogRef.current = d.id;
    // РОДНОЙ ЭКРАН ПЕРЕПИСКИ (трек Н): в приложении переписку рисует натив —
    // веб-слой не открываем вовсе, иначе на экране оказались бы ДВЕ ленты.
    // Мозг (данные, правила канала, отправка) остаётся у DialogPanel — он
    // монтируется без своей разметки.
    if (nativeMethod('chatSupported')) {
      // РОДНАЯ ЛЕНТА ВСТАЁТ НА НАЙДЕННУЮ РЕПЛИКУ (П5 обхода владельца 11.09.2026):
      // место в переписке едет мозгу ленты (DialogPanel), а он отдаёт его нативу
      // необязательным полем `focus` первой же страницы — ровно тем путём, каким на
      // реплику встаёт веб-шторка. Нового метода моста для этого не нужно.
      if (at) journalLog('msgs.hit.open', { id: d.id, at });
      setNativeDialog({ d, at });
      return;
    }
    // Карточка собеседника — нижним листом СТОПКОЙ поверх переписки (канон
    // NATIVE-SLIDE §11: не подмена; в вебе — слой-2 drawer, как «Информация»
    // модели). Имя аккаунта берём из списка аккаунтов селекта-фильтра.
    const accName = data?.accounts?.find(
      (a) => a.channel === d.channel && a.key === d.accountKey,
    )?.name;
    // Карточку открывает САМА переписка (событие): только у неё есть родное
    // поле (его надо снять — клавиатура нативная и рисуется поверх листа,
    // IMG_1293) и замок «Выключить автоматизации».
    const openInfo = () =>
      window.dispatchEvent(new CustomEvent('scr-msg-info', { detail: { id: d.id } }));
    panel.open({
      // Адрес переписки (ТЗ WEB-LOOK Э2.3, Закон 2): лист на телефоне держит /app/messages/<id>.
      href: pickToPath('messages', d.id),
      closeHref: pickToPath('messages', ''),
      title: dialogTitle(d) || t('noName'),
      // ЛОГИН — СТРОГО ПОД ИМЕНЕМ (владелец 2026-08-09, IMG_1522): раньше он
      // стоял в полосе над лентой, которая висела посреди экрана; полосы больше
      // нет, логин — второй строкой шапки (в приложении её рисует натив).
      ...(d.username ? { subtitle: `@${d.username}` } : {}),
      // ШАПКА — КАК У РОДНОГО ЭКРАНА ПЕРЕПИСКИ (паритет веб = приложение, владелец
      // 09.09.2026; эталон ChatViewController: системная шапка со скобкой «‹» слева,
      // имя и логин по центру, справа — КРУЖОК-ФОТО собеседника 38 в кружке 44, тап по
      // нему открывает карточку). Прежние крестик и голая «i» (05.08) сняты: переписка
      // въезжает сбоку как страница стопки, а не как окно, и закрывается «назад».
      headerLeft: (
        <KBareButton
          className="dr-close msg-dlg-hbtn"
          onClick={() => panel.close()}
          aria-label={tRoot('back')}
          title={tRoot('back')}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M14.5 5.5 8 12l6.5 6.5" />
          </svg>
        </KBareButton>
      ),
      headerRight: (
        <KBareButton
          className="dr-info msg-dlg-ava"
          onClick={openInfo}
          aria-label={t('infoTitle')}
          title={t('infoTitle')}
        >
          <DialogAvatar dialog={d} />
        </KBareButton>
      ),
      actionIcon: 'info',
      onAction: openInfo,
      body: (
        <DialogPanel
          dialog={d}
          onChanged={reload}
          onRead={readNow}
          onReadDone={readDone}
          account={accName}
          accountLive={
            liveAccounts.size > 0 ? liveAccounts.has(`${d.channel}:${d.accountKey}`) : undefined
          }
          {...(at ? { atMessage: at } : {})}
        />
      ),
    });
  };

  // ПРЯМОЙ ЗАХОД ПО АДРЕСУ ПЕРЕПИСКИ НА ТЕЛЕФОНЕ (ТЗ WEB-LOOK Э2.3, Закон 2): /app/messages/<id>
  // из ссылки или после Cmd+R обязан открыть саму переписку листом, а не голый список. В двух
  // и трёх слоях адрес ведёт колонка/поле (pick), здесь ничего не делаем. Один раз на адрес.
  const deepDialog = useRef<string | null>(null);
  useEffect(() => {
    if (pick !== null || !paneActive || readLayoutMode() !== 'phone') return;
    const id = pathToPick('messages', pathname);
    if (!id || id.startsWith('ch:') || deepDialog.current === id) return;
    const d = dialogs.find((x) => x.id === id);
    if (!d) return;
    deepDialog.current = id;
    // Отдельной задачей планировщика: drawer.open зовёт flushSync (см. scenarios.tsx).
    const t = window.setTimeout(() => openDialog(d), 0);
    return () => window.clearTimeout(t);
  });
  // Переписка, открытая РОДНЫМ экраном приложения: своей разметки у неё нет — она
  // кормит натив рядами и держит отправку (трек Н).
  const nativeOpen = nativeDialog ? (
    <DialogPanel
      // Открыли ту же переписку на ДРУГОЙ реплике — лента начинается заново (как у
      // веб-шторки): ключ несёт и место, иначе первая находка осталась бы на экране.
      key={nativeDialog.at ? `${nativeDialog.d.id}:${nativeDialog.at}` : nativeDialog.d.id}
      dialog={nativeDialog.d}
      onChanged={reload}
      onRead={readNow}
      onReadDone={readDone}
      account={
        data?.accounts?.find(
          (a) => a.channel === nativeDialog.d.channel && a.key === nativeDialog.d.accountKey,
        )?.name
      }
      accountLive={
        liveAccounts.size > 0
          ? liveAccounts.has(`${nativeDialog.d.channel}:${nativeDialog.d.accountKey}`)
          : undefined
      }
      {...(nativeDialog.at ? { atMessage: nativeDialog.at } : {})}
      nativeScreen
      onClose={() => setNativeDialog(null)}
    />
  ) : null;

  // ── РОДНАЯ ВКЛАДКА «ЧАТЫ» (docs/NATIVE-TABS-PLAN.md) ─────────────────────────────
  // В приложении со свежей сборкой СПИСОК переписок рисует натив (MsgsScreen.swift), а
  // этот раздел остаётся мозгом и запаской: живой опрос, отбор и слова — его, натив
  // получает готовые ряды и возвращает тапы. Вне приложения и на старых сборках хук
  // молчит — список живёт как раньше.
  // Признак — состоянием ПОСЛЕ маунта, не при рендере: isNativeApp на сервере всегда
  // false, гейт в рендере дал бы расхождение гидрации (тот же урок, что в settings.tsx).
  const [nativeDraws, setNativeDraws] = useState(false);
  useEffect(() => {
    // Кадром позже: синхронный setState в эффекте каскадит рендеры (правило хуков).
    const id = requestAnimationFrame(() => setNativeDraws(nativeMsgsSupported()));
    return () => cancelAnimationFrame(id);
  }, []);
  // ── ПРОГРЕВ ЛЕНТ: ВИДИМЫЕ РЯДЫ И ПОЛЭКРАНА ВПЕРЁД (PREFETCH-CAREFUL-TZ §2 п.1, владелец 11.10.2026) ──
  // Ряды и находки помечены `data-warm-id`; хук греет те, что попали в окно «видно + 50 % вниз», по одному разу.
  // Находка — своя страница ВОКРУГ реплики (`hit:<переписка>|<реплика>`). Родной список приложения рядов в DOM не
  // держит — там первые NATIVE_WARM_FIRST переписок (их и видно на первом экране).
  const warmInViewIds = useMemo(
    () => [...shown.map((d) => d.id), ...hits.map((h) => `hit:${h.dialogId}|${h.messageId}`)],
    [shown, hits],
  );
  const warmInView = useCallback(
    (id: string) => {
      if (!id.startsWith('hit:')) return warmFeed(id);
      const [did, at] = id.slice(4).split('|');
      if (did && at) warmDialogFeed(did, { at });
    },
    [warmFeed],
  );
  // Лента — только строке, пробывшей в окне ≥ WARM_QUEUE.inViewDwellMs (ТЗ SPEED §8.2: прокрутка мимо не греет);
  // касание — сразу, мимо порога (`warmFeed(id, true)`).
  useWarmInView(msgPager.wrapRef, warmInViewIds, warmInView, paneVisible && !nativeDraws, true);
  const nativeFirstIds = nativeDraws && paneActive ? shown.slice(0, NATIVE_WARM_FIRST).map((d) => d.id).join(',') : '';
  useEffect(() => {
    if (nativeFirstIds) nativeFirstIds.split(',').forEach((id) => warmFeed(id));
  }, [nativeFirstIds, warmFeed]);
  /**
   * СТРАНИЦЫ СОСЕДНИХ ПАПОК ДЛЯ СВАЙПА (правила 6–7 владельца 11.09.2026). Папки в
   * приложении листаются пейджером, и соседняя страница обязана быть не пустой ещё до
   * того, как палец её дотянет. Ряды берутся ИЗ КЭША кабинета тем же ключом, каким
   * собран свой список (`folderKey`) — второго запроса и второго кэша нет; греет их один
   * раз `useDialogList`, а сюда они доезжают сами, когда прогрев осел.
   * Во время поиска и без родного списка страниц нет вовсе (правило 8).
   */
  const pageFolders = useMemo(
    () => (needle || !nativeDraws ? [] : folderIds.filter((id) => id !== folder)),
    [needle, nativeDraws, folderIds, folder],
  );
  const pageKeys = useMemo(() => pageFolders.map(folderKey), [pageFolders, folderKey]);
  const pageCache = usePersistentPeek<MessagesResp>(pageKeys);
  // ФОТО СОБЕСЕДНИКОВ — ТЕМ ЖЕ СКЛАДОМ, ЧТО ЛИЦА ПАПОК И «КОНТАКТЫ» (native-photos.ts):
  // клиент ничего не качает сам, картинку готовит веб — JPEG 64 px (SCALE-PLAN Е1), ~3 КБ
  // на лицо; склад общий на кабинет, повторный показ той же папки лиц заново не готовит.
  // Пустая строка в словаре — фото не взялось: ряд рисуется буквой, ожидание не виснет.
  // Ряды НАХОДОК идут тем же списком (круг 11 п.17.2), значит и кружки собеседников у них
  // те же. ЛИЦО АККАУНТА, КОТОРЫЙ ВЕДЁТ ПЕРЕПИСКУ (П45) — тем же складом.
  // ФОТО СОСЕДНИХ СТРАНИЦ ЗАРАНЕЕ НЕ ГОТОВИМ (Е1): при шквале входящих каждый ответ
  // списка перекодировал бы лица трёх папок; соседняя страница до своих фото рисуется
  // кружками с буквами — как любой ряд без фото, и доберёт их своим показом.
  // ФОТО НАХОДОК ЗАНОВО НЕ ГОТОВИМ (15.2 п.7): во время поиска новые лица не заказываем —
  // находка берёт фото, если оно уже на складе (тот же человек есть в списке), иначе
  // буква. Иначе каждая буква поиска рождала бы пачку фото и патч.
  const photoUrls = Array.from(
    new Set(
      [
        ...shown.map((d) => d.avatarUrl),
        ...shown.map((d) => accOf(d.channel, d.accountKey)?.avatarUrl ?? ''),
      ].filter(Boolean),
    ),
  );
  const { photos: photosRaw, ready: photosAllReady } = useNativePhotos(
    nativeDraws ? photoUrls : [],
    64,
  );
  // ОДИН ПАТЧ С ФОТО (15.2 п.3): ряды уходят сразу буквами; фото прикладываются к рядам
  // разом — когда готовы все лица первой страницы, но не позже 400 мс после первого
  // готового. Так пересборок таблицы две: буквы и фото, а не по одной на каждую пачку.
  const photoKey = photoUrls.join('|');
  const [photosSettled, setPhotosSettled] = useState(false);
  const firstPhotoAt = useRef(0);
  useEffect(() => {
    firstPhotoAt.current = 0;
    setPhotosSettled(false);
  }, [photoKey]);
  const anyPhoto = photoUrls.some((u) => photosRaw[u] !== undefined);
  useEffect(() => {
    if (photosSettled || !nativeDraws) return;
    if (photosAllReady) {
      setPhotosSettled(true);
      return;
    }
    if (!anyPhoto) return;
    if (!firstPhotoAt.current) firstPhotoAt.current = Date.now();
    const left = Math.max(0, PHOTO_SETTLE_MS - (Date.now() - firstPhotoAt.current));
    const id = window.setTimeout(() => setPhotosSettled(true), left);
    return () => window.clearTimeout(id);
  }, [photosSettled, photosAllReady, anyPhoto, nativeDraws]);
  const photos = photosSettled ? photosRaw : EMPTY_PHOTOS;
  // ФОТО АККАУНТОВ ПАПОК (П65) — ТЕМ ЖЕ ЕДИНСТВЕННЫМ конвейером картинок, что лица рядов,
  // но кружком 40: в полосе папок фотография маленькая. Склад общий на кабинет, поэтому
  // «Контакты» те же кружки берут готовыми.
  const { photos: folderFaces } = useNativePhotos(
    folders.flatMap((f) => (f.account?.avatarUrl ? [f.account.avatarUrl] : [])),
    40,
  );
  const dark = useIsDark();
  // НАСТРОЙКИ ПАПОК — ОДНА РУКА (ТЗ M.3/M.5 п.5): в приложении со свежей сборкой это
  // родная полушторка, в браузере и на старых сборках — веб-шторка тем же составом.
  const foldersSettings = useFoldersSettings(accs, dark ? 'dark' : 'light');
  // ПРОСЬБА ИЗ «НАСТРОЕК» (владелец 13.09.2026, П85: пузырь «Папки» → «Чаты»): настройки
  // папок открывает САМ раздел — тем же ходом, что его шестерёнка. Просьба одноразовая:
  // раздел смонтирован — берёт её событием, не смонтирован — заберёт при монтировании
  // (переход сюда идёт следом).
  /**
   * РЯД РОДНОГО СПИСКА — ОДНА РУКА (правило 2 ТЗ 19.1): им собирается и своя папка, и
   * первые страницы соседних папок для свайпа. Второй разметки ряда не заводим.
   */
  const msgsRowOf = (d: DialogListRow): NativeMsgsRow => {
    const title = dialogTitle(d) || t('noName');
    const kindKey = KIND_KEY[d.lastKind];
    // Строка предпросмотра собирается ТОЙ ЖЕ рукой, что в веб-ряду (см. rows ниже):
    // «Вы: Фото · подпись», пусто и то и другое — «без текста». ИЩЕМ И СЛОВО СТОИТ В
    // ПОСЛЕДНЕЙ РЕПЛИКЕ (правило 1 владельца 09.09.2026) — вместо начала реплики едет
    // кусок вокруг слова и его позиция: натив рисует этот кусок жирным акцентом.
    // ГДЕ СОВПАЛО — ВИДНО В РЯДУ (П5 обхода владельца 11.09.2026): нашлось в логине или в
    // отсечённом хвосте имени — строка показывает именно это место, а не начало реплики.
    const { preview, mark } = searchPreview(d, title, needle, {
      you: t('you'),
      kind: kindKey ? t(kindKey) : '',
      noText: t('noText'),
    });
    // Совпадение в самом имени ряда — родной список рисует его жирным по этой позиции.
    const titleMark = markIn(title, needle);
    const tag = rowTags(d, liveAccounts)[0];
    const chan = CHANNEL_NAME[d.channel] ?? d.channel;
    const acc = accOf(d.channel, d.accountKey);
    return {
      id: d.id,
      title,
      preview,
      ...(mark ? { mark } : {}),
      ...(titleMark ? { titleMark } : {}),
      time: dialogTime(d.lastAt, locale, t('yesterday')),
      unread: d.unread,
      icon: `QuickTile-${chan}`,
      iconColor: channelHex(chan),
      account: showAcc ? (acc?.name ?? '') : '',
      tag: tag ? t(tag.key) : '',
      tagWarn: !!tag?.warn,
      photo: d.avatarUrl ? (photos[d.avatarUrl] ?? '') : '',
      // ЛИЦО АККАУНТА — ВСЕГДА, КОГДА АККАУНТ ИЗВЕСТЕН (владелец 12.09.2026, П45: «видеть,
      // кто ведёт»), а не только когда аккаунтов несколько, как у подписи `account`.
      // Фото нет — ключа нет, и ряд рисуется одним значком сети (буквы у аккаунта не бывает).
      ...(acc?.avatarUrl && photos[acc.avatarUrl] ? { accountPhoto: photos[acc.avatarUrl] } : {}),
      initial: contactInitial(title),
      initialColor: circleColor(title),
    };
  };
  const msgsRows: NativeMsgsRow[] = shown.map(msgsRowOf);
  // РЯДЫ НАХОДОК — ВСЛЕД ЗА ПЕРЕПИСКАМИ (круг 11 п.17.2 п.1, правило 8 владельца): в
  // приложении поиск обязан показывать не только «кто», но и «что нашлось». Порядок тот
  // же, что в вебе: сперва переписки, потом реплики. Ряд собирается ТОЙ ЖЕ рукой, что
  // веб-ряд находки ниже (hitRows), только жирный кусок веб рисует сам, а нативу уезжает
  // позицией слова в строке.
  const msgsHitRows: NativeMsgsRow[] = hits.map((h) => {
    const d = rowFromHit(h);
    const title = dialogTitle(d) || t('noName');
    const chan = CHANNEL_NAME[h.channel] ?? h.channel;
    const { preview, mark } = hitPreview(
      h.text,
      needle,
      h.direction === 'out' ? `${t('you')} ` : '',
    );
    const acc = accOf(h.channel, h.accountKey);
    return {
      id: hitRowId(h.messageId),
      title,
      preview,
      time: dialogTime(h.at, locale, t('yesterday')),
      // Находка — не новое сообщение: ни счётчика непрочитанных, ни пометки состояния.
      unread: 0,
      icon: `QuickTile-${chan}`,
      iconColor: channelHex(chan),
      account: showAcc ? (acc?.name ?? '') : '',
      tag: '',
      tagWarn: false,
      photo: h.avatarUrl ? (photos[h.avatarUrl] ?? '') : '',
      // Лицо аккаунта — и у ряда-находки (П45): ряд тот же, что у переписки.
      ...(acc?.avatarUrl && photos[acc.avatarUrl] ? { accountPhoto: photos[acc.avatarUrl] } : {}),
      initial: contactInitial(title),
      initialColor: circleColor(title),
      hitDialogId: h.dialogId,
      hitMessageId: h.messageId,
      ...(mark ? { mark } : {}),
    };
  });
  // Пустой список — ЧЕСТНАЯ строка теми же словами, что на веб-карточке ниже: при
  // активном отборе это «ничего не найдено», а не «переписок нет».
  const msgsFiltered =
    (state && state !== 'all') ||
    (channel && channel !== 'all') ||
    (account && account !== 'all') ||
    (mine && mine !== 'all') ||
    // ПАПКА СЧИТАЕТСЯ ОТБОРОМ, только когда у неё вправду есть условия: пустой раздел
    // у нового клиента честнее назвать «переписок пока нет».
    folderHasRules;
  // Ничего не нашлось — это когда пусто И у переписок, И у находок: реплика со словом
  // бывает и в переписке, которая по имени не подошла.
  // ПОКА ИДЁТ ПОИСК — НИ СЛОВА О ПУСТОТЕ (владелец 10.09.2026): ответ сервера на набранное
  // ещё в пути, и «Под фильтры ничего не попало» через секунду сменяется находками —
  // человек читает это как сбой. Список просто пуст, слова приходят вместе с ответом.
  const msgsEmpty =
    shown.length + msgsHitRows.length > 0 || searchPending
      ? ''
      : t(dialogs.length === 0 && !msgsFiltered ? 'empty' : 'filterEmpty');
  // ПОЛОСА ПАПОК — ОДНА НА ВЕБ И НА ПРИЛОЖЕНИЕ (ТЗ M.4): слова и цифры считает кабинет,
  // натив только рисует. Папок нет вовсе — полосы нет (поле не шлём), список идёт без
  // отбора.
  const nativeFolders =
    folders.length > 0
      ? {
          current: folder,
          // ПАПКА-АККАУНТ — КРУЖКАМИ (владелец 12.09.2026, П65): помеченной папке едут
          // значок сети, её цвет и фотография аккаунта base64 (натив рисует пару кружков
          // вместо слова). Фото ещё не готово — едут только значок и цвет, кружок фото
          // появится следующим патчем: свой список из-за картинки не ждёт.
          items: folders.map((f) => {
            const chan = f.account ? (f.account.channel === 'ig' ? 'instagram' : 'telegram') : '';
            const face = f.account?.avatarUrl ? (folderFaces[f.account.avatarUrl] ?? '') : '';
            return {
              id: f.id,
              name: f.name,
              unread: f.unread,
              ...(chan ? { icon: `QuickTile-${chan}`, iconColor: channelHex(chan) } : {}),
              ...(face ? { photo: face } : {}),
            };
          }),
          settingsLabel: tf('settings'),
        }
      : undefined;
  /**
   * ПЕРВЫЕ СТРАНИЦЫ СОСЕДНИХ ПАПОК (правила 6–7 владельца 11.09.2026): по записи на каждую
   * папку полосы, КРОМЕ текущей, в том же порядке. Ряды — той же рукой (`msgsRowOf`) из
   * кэша кабинета; кэш ещё не доехал — запись с пустыми рядами и БЕЗ слов (натив покажет
   * пустую страницу молча, слова приедут вместе с рядами, правило 7).
   */
  // Соседние страницы вооружаются через PAGES_AFTER_OPEN_MS после того, как свой список
  // готов (15.6); до того нативу уходит только текущая папка.
  const [pagesArmed, setPagesArmed] = useState(false);
  useEffect(() => {
    if (!nativeDraws || loading || pagesArmed) return;
    const id = window.setTimeout(() => setPagesArmed(true), PAGES_AFTER_OPEN_MS);
    return () => window.clearTimeout(id);
  }, [nativeDraws, loading, pagesArmed]);
  // ДОГРУЖЕННОЕ НЕ ПРОПАДАЕТ ПРИ СМЕНЕ ПАПКИ (владелец 16.09.2026, сборка 264: «долистал до
  // имени, свайп вправо-влево — и я на другом месте»). Папка, из которой ушли, помнит всё,
  // что в ней было догружено; соседняя страница получает этот список, а не первую страницу
  // из кэша, — ряды под пальцем не исчезают, место прокрутки остаётся. Находки поиска сюда
  // не пишутся: это не список папки.
  const msgsPages = nativeFolders
    ? pageFolders.map((id, i) => {
        const resp = pageCache[i];
        const rowsOfPage = withTail(folderKey(id), resp?.dialogs ?? []).map(msgsRowOf);
        // Слова пустой страницы — ТЕ ЖЕ, что у своего списка: отбор считается по тем же
        // меркам, только условия берутся у ЭТОЙ папки.
        const rules = Object.keys(folders.find((f) => f.id === id)?.rules ?? {}).length > 0;
        const filtered =
          (state && state !== 'all') ||
          (channel && channel !== 'all') ||
          (account && account !== 'all') ||
          (mine && mine !== 'all') ||
          rules;
        return {
          id,
          rows: rowsOfPage,
          emptyText: !resp || rowsOfPage.length > 0 ? '' : t(filtered ? 'filterEmpty' : 'empty'),
        };
      })
    : [];
  // РЕЖИМ ВЫБОРА ПЕРЕПИСОК (второй обход 10.09.2026, BACKLOG п.17б/в, образец — «Чаты»
  // Telegram): кнопка «Изменить» в шапке приложения, кружки-галочки у переписок и одно
  // действие внизу — «Прочитать (N)» / «Прочитать все». Удаления в режиме НЕТ.
  // Отсюда едут только СЛОВА: сам режим и отметки ведёт натив, а читает переписки веб
  // (onRead ниже). ПОКА ИДЁТ ПОИСК кнопки нет: «все» тогда означало бы не то, что на
  // экране (находки приходят своей ручкой), — так же и в Telegram.
  const nativeEdit = needle
    ? undefined
    : {
        // КОРОТКОЕ СЛОВО НА КАПСУЛЕ (П25 обхода владельца 11.09.2026, круг 3): в шапке
        // приложения слово стоит на стеклянной капсуле рядом с лупой, и «Изменить»
        // растягивает её через полшапки. Поэтому здесь «Изм.»; полное «Изменить»
        // остаётся у веб-кнопки шапки (shell.tsx) и у подписи кружка-галочки, где
        // сокращать слово нельзя.
        label: t('editShort'),
        doneLabel: tRoot('done'),
        // Метку `%n` натив заменит числом отмеченных — своих слов у него нет.
        readLabel: t('readSel', { n: '%n' }),
        readAllLabel: t('readAll'),
      };
  /**
   * ПРОЧИТАТЬ ПЕРЕПИСКИ — ОДНА РУКА (режим выбора приложения и его брат в браузере, П13
   * обхода владельца 11.09.2026). Отмеченные списком id, пустой список — «все в текущем
   * отборе» (отбор тот же, каким собран список: канал, аккаунт, состояние, автоматизации,
   * папка). Читает СЕРВЕР одной ручкой, дальше список освежается — счётчики опадают и на
   * веб-странице, и на родном экране.
   */
  const readDialogs = (ids: string[]) => {
    void (async () => {
      try {
        await fetch('/api/cabinet/messages', {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            read: true,
            ...(ids.length ? { ids } : {}),
            ...(channel !== 'all' ? { channel } : {}),
            ...(account !== 'all' ? { account } : {}),
            ...(state && state !== 'all' ? { state } : {}),
            ...(mine && mine !== 'all' ? { mine } : {}),
            ...(folder ? { folder } : {}),
          }),
        });
      } catch {
        /* обрыв сети — счётчики остаются как были, человек повторит */
      }
      // ЦИФРЫ ОБНОВЛЯЮТСЯ ВЕЗДЕ, А НЕ ТОЛЬКО В СПИСКЕ (П43 обхода владельца 12.09.2026:
      // «наклейки на строках гаснут, а цифры у папок остаются старыми»). Раньше здесь
      // перечитывался один список — папки и наклейка вкладки оставались со старым
      // числом. Зовём ТУ ЖЕ руку, что после закрытия переписки (readDone, П14 круга 2):
      // список, папки полосы и первые страницы соседних папок. Значок вкладки уедет
      // событием из эффекта списка, когда придёт новое `data.unread`, а значок
      // приложения считает сервер — его цифру опустил тот же PATCH выше.
      readDone();
    })();
  };
  useNativeMsgs({
    // ПЕРВЫЙ КАДР БЕЗ ОЖИДАНИЯ ФОТО (SCALE-PLAN Е1, владелец 15.09): список уходит нативу
    // сразу с кружками-буквами, фото доезжают следующим патчем, когда склад их отдаст.
    // Прежнее ожидание полных фото (приёмка 01.09) снято сознательно: на сотнях
    // переписок первый кадр ждал секунды.
    ready: !loading,
    theme: dark ? 'dark' : 'light',
    searchHint: t('search'),
    // Набранное слово плашка показывает вместо подсказки — как на «Ботах».
    query: msgQuery,
    rows: [...msgsRows, ...msgsHitRows],
    emptyText: msgsEmpty,
    // ВЕСЬ СПИСОК, А НЕ ПЕРВАЯ СТРАНИЦА (правило 2 владельца 09.09.2026): родной список
    // подходит к концу — просит следующую страницу тем же событием, ряды дописываются.
    // Выдача поиска не листается (её страниц у нас нет) — там признака «есть ещё» нет.
    more: !needle && hasMore,
    onMore: () => void loadMore(),
    ...(nativeFolders ? { folders: nativeFolders } : {}),
    // Страницы соседних папок — только когда их вправду есть чем листать (правило 8:
    // одна папка или поиск — свайпа нет).
    // СТРАНИЦЫ СОСЕДНИХ ПАПОК — ПОСЛЕ ПЕРВОГО КАДРА (SCALE-PLAN 15.6): в одном вызове
    // msgsOpen оболочка строила и свой список, и все соседние страницы — 994 мс на
    // главном потоке (журнал 16.09). Теперь открытие несёт только свой список, соседние
    // страницы доезжают патчем через PAGES_AFTER_OPEN_MS (свайп к соседке раньше этого
    // срока физически не успеть).
    ...(msgsPages.length > 0 && pagesArmed ? { pages: msgsPages } : {}),
    onFolder: (id) => setFolder(id),
    onFoldersOpen: () => foldersSettings.open(),
    ...(nativeEdit ? { edit: nativeEdit } : {}),
    // «Прочитать» из режима выбора — общей рукой (readDialogs выше).
    onRead: (ids) => readDialogs(ids),
    // ПАЛЕЦ ЛЁГ НА РЯД (П39): греем ленту, пока переписка ещё не открылась, — она выедет
    // уже с сообщениями. Открывает по-прежнему onRow: прогрев ничего не показывает.
    onRowDown: (id) => warmFeed(id, true),
    // Тап по ряду — та же рука, что у клика по веб-ряду: она и поднимает СУЩЕСТВУЮЩИЙ
    // родной экран переписки (chatOpen, трек Н). Своего пути открытия у списка нет.
    // Ряд НАХОДКИ отличается только приставкой ключа: открываем ту же переписку и просим
    // встать на найденной реплике — ровно как веб-ряд находки (hitRows ниже).
    onRow: (id) => {
      const mid = hitMessageIdOf(id);
      if (mid) {
        const h = hits.find((x) => x.messageId === mid);
        if (h) openDialog(rowFromHit(h), h.messageId);
        return;
      }
      const d = shown.find((x) => x.id === id);
      if (d) openDialog(d);
    },
    // ПОИСК — ОДИН В ОДИН С «БОТАМИ» (владелец 02.09.2026, Н5 фикслиста): плашка на
    // экране — ВХОД, тап открывает ту же РОДНУЮ строку поиска сверху, что и на «Ботах»;
    // набранное приходит общим событием searchQuery и ложится в тот же канал раздела
    // (MSG_Q_EVENT, shell.tsx). Отбирает по-прежнему useDialogList (сервер), второй
    // логики поиска не заводим.
    onSearchOpen: () => {
      void nativeBridge()?.openSearch?.({ placeholder: t('search') });
    },
  });
  // Список рисует натив — ВЕБ-ВЕРСИЮ ПОД НИМ НЕ РИСОВАТЬ ВОВСЕ (урок «Аккаунта»,
  // владелец 01.09.2026: веб «перещёлкивался» при уходе с вкладки и под шторками).
  // Мозг остаётся: открытая родная переписка кормится DialogPanel без своей разметки.
  // ЕДИНСТВЕННОЕ ИСКЛЮЧЕНИЕ — список не приехал НИ РАЗУ (связи нет): родного экрана
  // тогда ещё нет вовсе, и вместо пустой вкладки человек видит честную карточку «не
  // удалось загрузить» с повтором (кодов и внутренностей в ней нет — запрет №7).
  if (nativeDraws && !(!data && error)) return nativeOpen;

  // РАЗВОРОТ (Н5): всё, что на телефоне стоит списком, — в колонку порталом; поле — переписка.
  const side = (node: React.ReactNode) =>
    beside && sideSlot ? createPortal(<div className="msg-side">{node}</div>, sideSlot) : null;
  // Переписок нет (или ещё не выбрана) — подпись по центру поля, как в Telegram Desktop.
  const pickNote = <div className="msg-pick-note">{t('pickDialog')}</div>;

  // ПОЛОСА ПАПОК НАД СПИСКОМ — веб-версия (ТЗ M.3). Стоит и над ПУСТЫМ списком: иначе с
  // папки, под которую ничего не попало, было бы не сойти (тот же урок, что с полосой
  // капсул 11.08.2026). Папок нет вовсе — полосы нет.
  const filterRow =
    folders.length > 0 ? (
      // Полоса — в слот шапки списка на телефоне (ТЗ WEB-CAUSES О3, list-head.tsx HeadFolders).
      <HeadFolders>
        <FoldersStrip
          folders={folders}
          current={folder}
          // В режиме выбора полоса НЕ глушится: тап по папке и по шестерёнке сперва снимает
          // режим (как «Готово»), а потом делает своё дело (П13).
          onPick={msgPager.pick}
          link={msgPager.strip}
          onSettings={() => outOfEdit(() => foldersSettings.open())}
        />
      </HeadFolders>
    ) : null;

  if (loading) {
    // ПОЛОСА ПАПОК ЖИВЁТ И ПОКА ПАПКА ГРУЗИТСЯ (TARIFF-PAGE-TZ §11 П7, владелец 11.10.2026: «папки то нажимаются,
    // то нет»): прежде каркас шёл без полосы — тап по папке без кэша стирал полосу на время загрузки.
    // ЗАГЛУШКА ФОРМЫ (закон «экран показывается один раз», ред. 9 §2.3): ряды тех же высот, что у этого списка при
    // прошлом показе (форма с устройства → `shapes/chats.json` → сервер), вместо трёх ручных рядов-полосок.
    const sk = <Shape name="chats" />;
    const held = (
      <>
        {filterRow}
        {sk}
      </>
    );
    return beside ? side(held) : held;
  }
  if (!data && error) return <LoadError retry={reload} />;

  // Ряды есть, а обновить их не вышло (Ф13.4): честная строка над списком. Пока она
  // стоит, показанное — прежнее, а не свежее.
  const failNote = error ? <RefreshFailed retry={reload} /> : null;

  // ПУЗЫРЕЙ-КАТЕГОРИЙ НАД СПИСКОМ БОЛЬШЕ НЕТ (владелец 31.08.2026: «выглядят убого»).
  // Полоса чипов состояния (.tpl-flt-chips.msg-states) убрана целиком — позже её место
  // займут свои настраиваемые группы переписок. Сам отбор по состоянию жив: он остаётся
  // в колонке «Статус» на широком экране и в адресе (?state=), поэтому ни STATES,
  // ни onState обратно на экран не возвращать — это была именно эта полоса.

  // Заголовок правого поля на широком экране — сам отбор (как у «Продуктов»). Только в трёх
  // слоях: в двух (821–1023) лист = телефон, раздел подписывает шапка по центру, и под
  // плашкой поиска сразу папки и список (владелец 30.09.2026, ТЗ WEB-LOOK Э3).
  const paneT =
    layoutMode === 'three' ? (
      <div className="split-pane-t">
        {channel === 'tg' ? 'Telegram' : channel === 'ig' ? 'Instagram' : t('paneAll')}
      </div>
    ) : null;

  // ОТКРЫТАЯ ПЕРЕПИСКА (поле разворота и лист двух слоёв). Считается ДО пустых отборов: в
  // развороте пустой поиск в колонке не закрывает открытую справа переписку (Н5).
  // Переписка из ПОИСКА в загруженном начале списка может и не лежать — тогда её
  // строку знает сама находка (О22.5).
  const openD =
    pick && !pick.startsWith('ch:')
      ? (dialogs.find((x) => x.id === pick) ??
        shown.find((x) => x.id === pick) ??
        hits.filter((h) => h.dialogId === pick).map(rowFromHit)[0])
      : undefined;
  const openChat = openD ? (
    <div className="msg-2col">
      <div className="msg-2col-chat">
        <div className="split-msg">
          {/* key — чтобы при переходе к другой переписке лента и поле ответа
              начались заново. framed — рама чата ассистента. */}
          <DialogPanel
            key={atMsg?.dialog === openD.id ? `${openD.id}:${atMsg.message}` : openD.id}
            dialog={openD}
            onChanged={reload}
            onRead={readNow}
            onReadDone={readDone}
            framed
            {...(atMsg?.dialog === openD.id ? { atMessage: atMsg.message } : {})}
            account={
              data?.accounts?.find((a) => a.channel === openD.channel && a.key === openD.accountKey)
                ?.name
            }
            accountLive={
              liveAccounts.size > 0
                ? liveAccounts.has(`${openD.channel}:${openD.accountKey}`)
                : undefined
            }
            {...(beside ? {} : { back: () => onPick?.(lastGroup.current) })}
          />
        </div>
      </div>
    </div>
  ) : null;

  // Пустой раздел — карточка-подсказка (диалоги заводятся сами, когда человек напишет).
  // ФИЛЬТРЫ СЕРВЕРНЫЕ: при активном отборе пустой ответ — это «ничего не найдено», а не
  // «переписок нет» (владелец 11.08.2026, IMG_1639: полоса капсул пропадала вместе со
  // списком, и с пустого отбора было не сойти) — капсулы остаются, текст честный.
  if (dialogs.length === 0 && !(needle && hits.length > 0)) {
    const filtered =
      (state && state !== 'all') ||
      (channel && channel !== 'all') ||
      (account && account !== 'all') ||
      (mine && mine !== 'all') ||
      folderHasRules;
    // Пусто — строкой 15 вторичным в 40 под плашкой, без карточки (MsgsScreen.note;
    // паритет веб = приложение, 09.09.2026). Идёт поиск, а ответ ещё в пути (searchPending)
    // — строки нет вовсе: тем же молчанием, что и у родного списка (msgsEmpty выше).
    const card = searchPending ? null : (
      <ListEmptyNote text={t(filtered ? 'filterEmpty' : 'empty')} />
    );
    if (beside)
      return (
        <>
          {side(
            <>
              {filterRow}
              <FolderPage pager={msgPager}>
                {failNote}
                {card}
              </FolderPage>
            </>,
          )}
          {openChat ?? pickNote}
        </>
      );
    return pick === null ? (
      <>
        {filterRow}
        <FolderPage pager={msgPager}>
          {failNote}
          {card}
        </FolderPage>
      </>
    ) : (
      <>
        {paneT}
        {filterRow}
        <FolderPage pager={msgPager}>
          {failNote}
          {card}
        </FolderPage>
      </>
    );
  }

  // Под фильтр/поиск ничего не попало: подсказка КАРТОЧКОЙ (как пустой раздел выше).
  // Пустую группу списка не рисуем — она торчала голой белой капсулой под подписью
  // (скрин владельца 2026-08-02, родной поиск).
  // НАХОДКИ В РЕПЛИКАХ — НЕ «НИЧЕГО НЕ НАЙДЕНО» (кадр iOS C02-chats-search-results, сверка
  // П12 03.10): слово есть только внутри переписок, а не в имени/последней реплике — у iOS
  // в выдаче ряды находок, у веба стояла пустая заглушка и находки сервера не доезжали до
  // экрана. Есть находки — идём дальше: список рисует только их (rows() пустым не встаёт).
  if (shown.length === 0 && !(needle && hits.length > 0)) {
    const card = searchPending ? null : <ListEmptyNote text={t('filterEmpty')} />;
    if (beside)
      return (
        <>
          {side(
            <>
              {filterRow}
              <FolderPage pager={msgPager}>
                {failNote}
                {card}
              </FolderPage>
            </>,
          )}
          {openChat ?? pickNote}
        </>
      );
    return pick === null ? (
      <>
        {filterRow}
        <FolderPage pager={msgPager}>
          {failNote}
          {card}
        </FolderPage>
      </>
    ) : (
      <>
        {paneT}
        {filterRow}
        <FolderPage pager={msgPager}>
          {failNote}
          {card}
        </FolderPage>
      </>
    );
  }

  // Ряды переписок общие для узкого и широкого экрана (владелец 2026-08-03: список
  // переехал из колонки в правое поле). Узкий экран открывает переписку шторкой,
  // широкий — лентой НА МЕСТЕ списка (адрес /app/messages/<id>).
  // Ряды переписок — общий рендер: главный список И левая колонка двухпанельного
  // режима (владелец 05.08: «слева список контактов, справа чат»). active — открытая
  // переписка (подсветка ряда в колонке).
  // Рука открытия — в ref: эффект «переписка по адресу» выше объявлен раньше самой руки.
  openDialogRef.current = openDialog;

  const rows = (active?: string) => (
    <div className="ai-vgroups" style={{ marginBottom: active ? 0 : 26 }} data-shape="chats">
      <div className="ai-vgroup">
        {shown.map((d, rowIx) => {
          const title = dialogTitle(d) || t('noName');
          // Сеть переписки — значком у имени (П32 круга 4). Имя аккаунта из ряда ушло:
          // его место — карточка собеседника (решение владельца 11.09.2026).
          const chan = CHANNEL_NAME[d.channel] ?? d.channel;
          const kindKey = KIND_KEY[d.lastKind];
          // «Вы: Фото · подпись» — вид вложения впереди текста, как в списке диалогов
          // мессенджера: по одной строке видно, что пришло, даже без подписи.
          // Пусто и то и другое (репост без подписи, вложение без имени) — «без
          // текста»: строка «Вы:» без продолжения выглядела обрывом (скрин владельца
          // 2026-08-06).
          const words = {
            you: t('you'),
            kind: kindKey ? t(kindKey) : '',
            noText: t('noText'),
          };
          // ГДЕ СОВПАЛО — ВИДНО В РЯДУ (П5 обхода владельца 11.09.2026):
          //   в реплике — кусок вокруг слова, само слово жирным акцентом (приставка
          //     «Вы Фото ·» остаётся обычным текстом — выделяется только найденное);
          //   в имени — жирным само имя строкой выше;
          //   в логине или в отсечённом хвосте имени — вместо реплики ряд показывает
          //     это место (@логин, полное имя), тоже с выделением.
          const { preview, where } = searchPreview(d, title, needle, words);
          // В режиме выбора тап по ряду не открывает переписку, а ставит галочку (П13).
          const open = () => (editing ? toggleMark(d.id) : openDialog(d));
          // РЯД — ОДИН В ОДИН С РОДНЫМ СПИСКОМ (паритет веб = приложение, владелец
          // 09.09.2026; эталон — MsgsScreen.swift, MsgsRowControl): ДВЕ строки слов —
          //   1) имя 17, сразу за ним значок сети 14 цветом сети, время 12 третичным
          //      справа;
          //   2) последняя реплика 13 вторичным ВСЕГДА высотой в две строки (даже если
          //      текст в одну), справа на уровне её второй строки — наклейка
          //      непрочитанных капсулой 18.
          // Третьей строки «значок · @аккаунт» больше нет (П32 круга 4 обхода владельца
          // 11.09.2026): каким аккаунтом ведётся переписка, человек смотрит в карточке
          // собеседника.
          // Слов состояния («нельзя писать», «пропущен ответ») в ряду НЕТ — приложение
          // сняло их 06.09.2026, причину человек читает в самой переписке; `rowTags`
          // по-прежнему считается для родного списка (поле tag договора моста).
          return (
            <div
              className={`scn-trow msg-trow${active === d.id ? ' on' : ''}${editing ? ' is-pick' : ''}`}
              key={d.id}
              data-warm-id={d.id}
            >
              <div
                className="scn-trow-main"
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onClick={open}
                // ЛЕНТА ГРЕЕТСЯ ДО ОТКРЫТИЯ (П39, браузерный конец того же правила):
                // мышь навели или палец лёг — страница переписки уже едет, и шторка
                // открывается с сообщениями. Одна переписка греется один раз за заход,
                // лишних запросов от долгого наведения не будет.
                onPointerDown={() => warmFeed(d.id, true)}
                onMouseEnter={() => warmFeed(d.id, true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') open();
                }}
              >
                {/* Кружок-галочка режима выбора — тот же элемент набора, что у списка
                      людей «Кому» (своей галочки не рисуем). Нажатие по самой галочке
                      дальше не идёт: иначе ряд переключил бы отметку второй раз и она
                      осталась бы как была. */}
                {editing ? (
                  <span
                    className="msg-pick"
                    role="presentation"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <KCheckbox
                      checked={markedSet.has(d.id)}
                      ariaLabel={t('editStart')}
                      onChange={() => toggleMark(d.id)}
                    />
                  </span>
                ) : null}
                <DialogAvatar dialog={d} eager={rowIx < EAGER_AVATARS} />
                <div className="msg-w">
                  <div className="msg-l1">
                    <span className="msg-l1n">
                      <span className="msg-name">
                        <Marked text={title} needle={needle} />
                      </span>
                    </span>
                    {/* ЗНАЧОК СЕТИ И ЛИЦО АККАУНТА — СПРАВА ОТ ВРЕМЕНИ (владелец 12.09.2026):
                          одной вертикалью у правого края во всех строках, а не за именем
                          разной длины. Тот же порядок у родного ряда (line1). */}
                    <span className="msg-l1r">
                      <span className="msg-time">
                        {dialogTime(d.lastAt, locale, t('yesterday'))}
                      </span>
                      <span className="msg-ch" aria-hidden style={{ color: channelHex(chan) }}>
                        <ChannelIcon channel={chan} size={14} />
                      </span>
                      <AccountAvatar url={accOf(d.channel, d.accountKey)?.avatarUrl} />
                    </span>
                  </div>
                  <div className="msg-b">
                    <div className="msg-prev">
                      {where === 'text' ? (
                        <>
                          {previewPrefix(d, words)}
                          <Snippet text={d.lastText} needle={needle} />
                        </>
                      ) : where === 'username' || where === 'name' ? (
                        <Marked text={preview} needle={needle} />
                      ) : (
                        preview || t('noText')
                      )}
                    </div>
                    {d.unread > 0 && <span className="msg-unread">{d.unread}</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // РЯДЫ НАХОДОК (О22.5, владелец 17.08: «в результатах показывать такой же пузырь:
  // слева аватарка, имя и текст, в котором найдено слово; при нажатии открывать переписку
  // в нужном месте»). Вид ряда общий со списком — своего не заводим.
  const hitRows = () => (
    <div className="ai-vgroups" style={{ marginBottom: 26 }}>
      <div className="ai-vgroup">
        {hits.map((h) => {
          const d = rowFromHit(h);
          const hitChan = CHANNEL_NAME[d.channel] ?? d.channel;
          const open = () => openDialog(d, h.messageId);
          return (
            <div className="scn-trow msg-trow" key={h.messageId} data-warm-id={`hit:${h.dialogId}|${h.messageId}`}>
              <div
                className="scn-trow-main"
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onClick={open}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') open();
                }}
              >
                <DialogAvatar dialog={d} />
                {/* Тот же ряд, что у списка (П32): имя со значком сети, реплика всегда
                    высотой в две строки. Наклейки непрочитанных у находки нет
                    (§17.3 п.3: это одна реплика, а не переписка). */}
                <div className="msg-w">
                  <div className="msg-l1">
                    <span className="msg-l1n">
                      <span className="msg-name">{dialogTitle(d) || t('noName')}</span>
                    </span>
                    {/* Значок сети и лицо аккаунта — справа от времени (12.09.2026), как у переписки. */}
                    <span className="msg-l1r">
                      <span className="msg-time">{dialogTime(h.at, locale, t('yesterday'))}</span>
                      <span className="msg-ch" aria-hidden style={{ color: channelHex(hitChan) }}>
                        <ChannelIcon channel={hitChan} size={14} />
                      </span>
                      <AccountAvatar url={accOf(d.channel, d.accountKey)?.avatarUrl} />
                    </span>
                  </div>
                  <div className="msg-b">
                    <div className="msg-prev">
                      {h.direction === 'out' ? `${t('you')} ` : ''}
                      <Snippet text={h.text} needle={needle} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ОДНА КНОПКА ВНИЗУ В РЕЖИМЕ ВЫБОРА (П13): ничего не отмечено — «Прочитать все» (весь
  // текущий отбор, как в приложении), отмечено — «Прочитать (N)». Полоса — элемент набора
  // (KActionBar), своей плавающей плашки не рисуем.
  // На телефоне — ПЛАВАЮЩАЯ БЕЛАЯ ПИЛЮЛЯ над таббаром (ТЗ WEB-PARITY-3 Ч6/Ч7, кадр iOS
  // C01-chats-edit-mode): класс `msg-readbar` перекладывает полосу набора (cabinet.css).
  const readBar = editing ? (
    <div className="msg-readbar">
      <KActionBar>
        <KButton
          block
          tone="primary"
          onClick={() => {
            readDialogs(marked);
            leaveEdit();
          }}
        >
          {marked.length > 0 ? t('readSel', { n: marked.length }) : t('readAll')}
        </KButton>
      </KActionBar>
    </div>
  ) : null;

  const listOf = (active?: string) => (
    <>
      {nativeOpen}
      {filterRow}
      <FolderPage pager={msgPager}>
        {failNote}
        {shown.length > 0 ? rows(active) : null}
        {/* Найденные реплики — под перепиской, найденной по имени собеседника: сначала
          «кто», потом «где сказано». Ничего не нашлось вовсе — обычная пустая карточка
          списка выше говорит об этом сама. */}
        {needle && hits.length > 0 && hitRows()}
        {/* АВТОЗАГРУЗКА ВМЕСТО «ПОКАЗАТЬ ЕЩЁ» (владелец 17.08, О22.4). Список догружается
          сам, когда конец подъезжает к экрану; кнопки нет ни в вебе, ни в приложении.
          Ловушка стоит ЗА последним рядом с запасом в пол-экрана — так следующая страница
          успевает приехать до того, как человек упрётся в конец. */}
        {hasMore && !needle && (
          <div className="msg-more sk-hold" ref={moreRef} aria-busy={busy}>
            <div className="scn-trow msg-trow">
              <div className="scn-trow-main">
                <Sk w={40} h={40} r="round" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  <Sk w={140} h={13} />
                  <Sk w={200} h={11} />
                </div>
              </div>
            </div>
          </div>
        )}
      </FolderPage>
      {/* Плавающая пилюля «Прочитать» — вне страницы папки: страница на ходу несёт transform,
          а fixed-слой внутри него ехал бы вместе с ней. */}
      {readBar}
    </>
  );

  // Открытая переписка в поле (Н5): в развороте список рядом, в колонке, — тут только
  // лента; в двух слоях — лента на месте списка с «‹» (как телефон).
  if (openChat) {
    if (beside)
      return (
        <>
          {side(listOf(openD?.id))}
          {openChat}
        </>
      );
    return openChat;
  }
  // Переписки по адресу в загруженных страницах нет (чужая/устаревшая ссылка) —
  // показываем список отбора, не пустую стену.

  if (beside)
    return (
      <>
        {side(listOf())}
        {pickNote}
      </>
    );
  return pick === null ? (
    listOf()
  ) : (
    <>
      {paneT}
      {listOf()}
    </>
  );
}
